import { useState, useEffect, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { supabase } from "./supabaseClient"

const V = "#7C3AED"
const V_SOFT = "#F3F0FF"
const V_MID = "#EDE9FE"
const WHITE = "#FFFFFF"
const OFF = "#FAFAFA"
const TEXT = "#09090B"
const MUTED = "#71717A"
const BORDER = "#E4E4E7"
const SUCCESS = "#059669"
const SUCCESS_BG = "#ECFDF5"
const SUCCESS_MID = "#D1FAE5"
const H = "'Plus Jakarta Sans', system-ui, sans-serif"
const B = "'Manrope', system-ui, sans-serif"


// ─── CAMPAIGN TYPES ───────────────────────────────────────────────────────────
const CAMPAIGN_TYPES = {
  es: [
    { id:"lanzamiento",   label:"Lanzamiento",         desc:"Producto o servicio nuevo" },
    { id:"oferta",        label:"Oferta / Descuento",  desc:"Precio especial por tiempo limitado" },
    { id:"restock",       label:"Regresó / Restock",   desc:"El producto volvió" },
    { id:"temporada",     label:"Temporada / Fecha",   desc:"Navidad, día de la madre, etc." },
    { id:"testimonial",   label:"Testimonial",         desc:"Resultado real de un cliente" },
    { id:"educativo",     label:"Educativo",           desc:"Cómo se usa, por qué funciona" },
    { id:"ugc",           label:"UGC Style",           desc:"Parece grabado por un usuario real" },
    { id:"pov",           label:"POV",                 desc:"Punto de vista del cliente" },
    { id:"bts",           label:"Behind the Scenes",   desc:"El proceso, cómo se hace" },
    { id:"antesdespues",  label:"Antes y Después",     desc:"Transformación visible" },
  ],
  en: [
    { id:"lanzamiento",   label:"Launch",              desc:"New product or service" },
    { id:"oferta",        label:"Offer / Discount",    desc:"Special price for limited time" },
    { id:"restock",       label:"Back in Stock",       desc:"The product is back" },
    { id:"temporada",     label:"Season / Date",       desc:"Christmas, mother's day, etc." },
    { id:"testimonial",   label:"Testimonial",         desc:"Real client result" },
    { id:"educativo",     label:"Educational",         desc:"How to use it, why it works" },
    { id:"ugc",           label:"UGC Style",           desc:"Looks recorded by a real user" },
    { id:"pov",           label:"POV",                 desc:"Client's point of view" },
    { id:"bts",           label:"Behind the Scenes",   desc:"The process, how it's made" },
    { id:"antesdespues",  label:"Before & After",      desc:"Visible transformation" },
  ]
}

const FOCUSES = {
  es: [
    { id:"posicionamiento", label:"Posicionamiento",   desc:"Que me conozcan y recuerden" },
    { id:"seguidores",      label:"Crecer seguidores", desc:"Atraer nueva audiencia" },
    { id:"ventas",          label:"Generar ventas",    desc:"Convertir y cerrar" },
    { id:"lanzamiento",     label:"Lanzamiento",       desc:"Producto, servicio o novedad" },
    { id:"comunidad",       label:"Comunidad",         desc:"Conectar con mi audiencia" },
    { id:"educar",          label:"Educar",            desc:"Posicionarme como experto" },
  ],
  en: [
    { id:"posicionamiento", label:"Positioning",       desc:"Get known and remembered" },
    { id:"seguidores",      label:"Grow followers",    desc:"Attract new audience" },
    { id:"ventas",          label:"Generate sales",    desc:"Convert and close" },
    { id:"lanzamiento",     label:"Launch",            desc:"Product, service or news" },
    { id:"comunidad",       label:"Community",         desc:"Connect with my audience" },
    { id:"educar",          label:"Educate",           desc:"Position as expert" },
  ]
}

const DAYS = {
  es: ["Lunes","Martes","Miércoles","Jueves","Viernes","Sábado","Domingo"],
  en: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
}

const FORMAT_COLORS = {
  Post:     { bg:"#F0FDF4", color:"#16A34A" },
  Carrusel: { bg:"#EFF6FF", color:"#2563EB" },
  Carousel: { bg:"#EFF6FF", color:"#2563EB" },
  Reel:     { bg:"#FDF4FF", color:"#9333EA" },
  Historia: { bg:"#FFF7ED", color:"#EA580C" },
  Story:    { bg:"#FFF7ED", color:"#EA580C" },
  Video:    { bg:"#FEF2F2", color:"#DC2626" },
}

const FORMAT_DIMS = {
  Post:     { size:"1024×1024", label:"1:1 · Instagram, Facebook" },
  Carrusel: { size:"1024×1024", label:"1:1 · Instagram, Facebook" },
  Carousel: { size:"1024×1024", label:"1:1 · Instagram, Facebook" },
  Reel:     { size:"1024×1792", label:"9:16 · Reels, TikTok" },
  Historia: { size:"1024×1792", label:"9:16 · Historia, TikTok" },
  Story:    { size:"1024×1792", label:"9:16 · Story, TikTok" },
  Video:    { size:"1024×1792", label:"9:16 · Vertical" },
}

const IS_VIDEO = (fmt) => ["Reel","Historia","Story","Video"].includes(fmt)

const LOAD_MSGS = {
  es: ["Leyendo tu marca...","Analizando tu audiencia...","Construyendo la estrategia...","Generando hooks con tu voz de marca...","Preparando CTAs estratégicos...","Creando guiones para videos...","Diseñando conceptos de imagen..."],
  en: ["Reading your brand...","Analyzing your audience...","Building the strategy...","Generating hooks in your brand voice...","Preparing strategic CTAs...","Creating video scripts...","Designing image concepts..."],
}

const IMAGEN_GRADIENTS = {
  Editorial:   "linear-gradient(135deg,#F5F0E8,#E8DDD0)",
  Lifestyle:   "linear-gradient(135deg,#FFF8F0,#FFD9C0)",
  Minimalista: "linear-gradient(135deg,#F0F0F5,#E0E0F0)",
  Ilustración: "linear-gradient(135deg,#FFF0F8,#FFD6F0)",
  Cinematic:   "linear-gradient(135deg,#1A1A2E,#2D2D4E)",
  Bold:        "linear-gradient(135deg,#3D1A6E,#7C3AED)",
}
const IS_DARK = (est) => ["Cinematic","Bold"].includes(est)

function ImageMockup({ estilo, nombre, concepto, formato, small=false, hasProduct=false, hasPerson=false }) {
  const grad = IMAGEN_GRADIENTS[estilo] || IMAGEN_GRADIENTS["Editorial"]
  const dark = IS_DARK(estilo)
  const dims = FORMAT_DIMS[formato] || FORMAT_DIMS["Post"]
  const isVert = dims.size === "1024×1792"
  const h = small ? (isVert ? 240 : 220) : (isVert ? 320 : 240)
  return (
    <div style={{ background:grad, borderRadius:small?8:12, height:h, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:small?"8px":"1.25rem", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", inset:0, opacity:.08, backgroundImage:"radial-gradient(circle at 20% 80%,#fff 0%,transparent 50%)" }}/>
      {(hasProduct||hasPerson) && (
        <div style={{ position:"absolute", top:small?4:8, left:small?6:10, fontSize:9, background:"rgba(0,0,0,.3)", color:"#fff", borderRadius:10, padding:"2px 7px", fontFamily:B, letterSpacing:".06em" }}>
          {hasProduct?"Producto":"Persona"}
        </div>
      )}
      <div style={{ position:"relative", zIndex:1, textAlign:"center" }}>
        <div style={{ fontFamily:H, fontSize:small?10:16, fontWeight:800, color:dark?WHITE:"#1A1A18", letterSpacing:"-.01em", marginBottom:small?2:4 }}>{nombre}</div>
        {!small && <div style={{ width:20, height:1.5, background:dark?"rgba(255,255,255,.4)":"rgba(0,0,0,.15)", margin:"0 auto .4rem", borderRadius:1 }}/>}
        {!small && concepto && <div style={{ fontSize:10, color:dark?"rgba(255,255,255,.55)":MUTED, fontStyle:"italic", maxWidth:180, lineHeight:1.45, fontFamily:B }}>{concepto.slice(0,80)}{concepto.length>80?"...":""}</div>}
      </div>
      <div style={{ position:"absolute", bottom:small?4:8, right:small?6:10, fontSize:8, color:dark?"rgba(255,255,255,.4)":"rgba(0,0,0,.2)", fontFamily:B }}>{dims.label}</div>
      <div style={{ position:"absolute", bottom:small?4:8, left:small?6:10, fontSize:8, color:dark?"rgba(255,255,255,.4)":"rgba(0,0,0,.2)", fontFamily:B }}>{estilo}</div>
    </div>
  )
}

export default function KorivaWeekly() {
  const [lang, setLang]         = useState("es")
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [semanaActualId, setSemanaActualId] = useState(null)
  const [personajes, setPersonajes] = useState([])
  const [personajeElegido, setPersonajeElegido] = useState(null)
  // Multi-seleccion: array de IDs de personajes elegidos (1, 2 o 3). Reemplaza poco a poco a personajeElegido.
  const [personajesElegidos, setPersonajesElegidos] = useState([])
  // Logo del proyecto (del brand_form): URL si existe, y si el usuario quiere incluirlo en la imagen
  const [logoProyecto, setLogoProyecto] = useState(null)
  const [incluirLogo, setIncluirLogo] = useState(false)
  const [iconoAI, setIconoAI] = useState(null)
  // Texto opcional en la imagen + fuente + posicion (el modelo lo escribe en la imagen generada)
  const [textoImagen, setTextoImagen] = useState("")
  const [fuenteImagen, setFuenteImagen] = useState(null)
  const [posicionTexto, setPosicionTexto] = useState("abajo") // arriba | abajo | lado
  // Las 10 fuentes que el modelo domina bien (validadas con ChatGPT)
  const FUENTES_DISPONIBLES = [
    { id:"Montserrat",         label:"Moderna" },
    { id:"Poppins",            label:"Amigable" },
    { id:"Helvetica",          label:"Limpia" },
    { id:"Lato",               label:"Profesional" },
    { id:"Playfair Display",   label:"Elegante" },
    { id:"Cormorant Garamond", label:"Editorial" },
    { id:"Bebas Neue",         label:"Impacto" },
    { id:"Anton",              label:"Bold" },
    { id:"Oswald",             label:"Condensada" },
    { id:"Lobster",            label:"Retro" },
    { id:"Pacifico",           label:"Script" },
    { id:"Dancing Script",     label:"Caligrafica" },
    { id:"Great Vibes",        label:"Femenina" },
    { id:"Caveat",             label:"Handwriting" },
    { id:"Amatic SC",          label:"Manuscrita" }
  ]
  // Helper para marcar/desmarcar un personaje en el array
  const toggleCharacter = (id) => {
    setPersonajesElegidos(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }
  const [project, setProject] = useState(null)
  useEffect(() => {
    const loadProject = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { navigate("/login"); return }
      const { data: proj, error } = await supabase
        .from("projects")
        .select("id, nombre")
        .eq("user_id", session.user.id)
        .eq("activo", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .single()
      if (error || !proj) { navigate("/onboarding"); return }
      setProject(proj)
      // Cargar personajes del proyecto (para reusarlos en las imagenes)
      const { data: chars } = await supabase
        .from("characters").select("id, nombre, imagen_url").eq("project_id", proj.id).order("created_at", { ascending: false })
      if (chars) setPersonajes(chars)
      // Cargar el logo del proyecto del brand_form (solo si existe en R2)
      const { data: bf } = await supabase
        .from("brand_forms").select("logo, logo_url").eq("project_id", proj.id).single()
      if (bf) {
        // Preferimos logo_url (R2) para la previsualizacion; si no, el base64 viejo (logo)
        setLogoProyecto({ url: bf.logo_url || null, base64: bf.logo || null, listoParaUsar: !!bf.logo_url })
      }
      const { data: u } = await supabase
        .from("users")
        .select("img_credits_total, img_credits_used, img_credits_extra, lang")
        .eq("id", session.user.id)
        .single()
      if (u) {
        if (u.lang) setLang(u.lang)
        setImgTotal((u.img_credits_total || 0) + (u.img_credits_extra || 0))
        setImgUsed(u.img_credits_used || 0)
      }
    }
    loadProject()
  }, [navigate])

  // Si viene un id de semana en la URL (?week=...), cargar esa semana guardada
  useEffect(() => {
    const weekId = searchParams.get("week")
    if (!weekId || !project) return
    async function abrirSemana() {
      try {
        const { data: semana } = await supabase
          .from("weeks").select("*").eq("id", weekId).single()
        if (semana) {
          const dias = (semana.dias || []).map(day => ({
            ...day,
            dimensions: FORMAT_DIMS[day.formato] || FORMAT_DIMS["Post"],
            img_prompt_auto: day.img_prompt_auto || buildImgPrompt(day),
            img_approved: !!(day.img_url || (day.img_urls && day.img_urls.length > 0)),
          }))
          setResult({ ...semana, dias, week_id: semana.id, resumen_semana: semana.resumen_semana })
          setMode(semana.modo || "brand")
          setActiveDay(0)
          setPhase("results")
        }
      } catch (e) {
        console.error("Error abriendo semana guardada:", e)
      }
    }
    abrirSemana()
  }, [searchParams, project])

  // ============================================================
  // ⚠️ REGLA DEL CALENDARIO PAUSADA PARA PRUEBAS (26 mayo)
  // Para REACTIVAR: cambiar la siguiente linea a  const REGLA_CALENDARIO_ACTIVA = true
  // ============================================================
  const REGLA_CALENDARIO_ACTIVA = true

  // Regla: 1 estrategia por semana de calendario. Si ya hay una de esta semana, bloquear (salvo que venga ?week=)
  useEffect(() => {
    if (!REGLA_CALENDARIO_ACTIVA) return // PRUEBAS: regla pausada, dejar crear libremente
    if (!project) return
    if (searchParams.get("week")) return // viene a ver una semana guardada, no bloquear
    async function revisarSemanaActual() {
      try {
        // Regla justa: bloquear si la ultima semana se creo hace menos de 7 dias
        const hace7dias = new Date()
        hace7dias.setDate(hace7dias.getDate() - 7)
        const { data: semanaActual } = await supabase
          .from("weeks")
          .select("id")
          .eq("project_id", project.id)
          .gte("created_at", hace7dias.toISOString())
          .order("created_at", { ascending: false })
          .limit(1)
        if (semanaActual && semanaActual.length > 0) {
          setSemanaActualId(semanaActual[0].id)
          setPhase("ya_tiene_semana")
        }
      } catch (e) {
        console.error("Error revisando semana actual:", e)
      }
    }
    revisarSemanaActual()
  }, [project, searchParams])
  const [phase, setPhase]       = useState("mode") // mode | focus | product_setup | person_setup | generating | results | imgflow
  const [mode, setMode]         = useState(null)   // "brand" | "product" | "person"
  const [focus, setFocus]       = useState(null)
  const [temas, setTemas]               = useState(null)
  const [temasLoading, setTemasLoading] = useState(false)
  const [temaElegido, setTemaElegido]   = useState(null)
  const [tema, setTema]         = useState("")
  // Product campaign
  const [productPhoto, setProductPhoto] = useState(null)
  const [productAnalysis, setProductAnalysis] = useState(null)
  const [productName, setProductName] = useState("")
  const [productDesc, setProductDesc] = useState("")
  const [campaignType, setCampaignType] = useState(null)
  const [campaignContext, setCampaignContext] = useState("")
  const [analyzingProduct, setAnalyzingProduct] = useState(false)
  // Person campaign
  const [personPhoto, setPersonPhoto] = useState(null)
  const [personRole, setPersonRole] = useState("")
  const [personEmotion, setPersonEmotion] = useState("")
  const [personContext, setPersonContext] = useState("")
  // Results
  const [result, setResult]     = useState(null)
  const [activeDay, setActiveDay] = useState(0)
  const [copied, setCopied]     = useState(null)
  const [loadMsg, setLoadMsg]   = useState(0)
  // Image flow
  const [imgPhase, setImgPhase] = useState("prompt")
  const [wellnessIdx, setWellnessIdx] = useState(0)
  const wellnessMsgs = {
    es: [
      "Respira profundo tres veces, suelta los hombros",
      "Mientras esperamos, estírate suavemente",
      "KORIVA está creando algo único para ti",
      "Tómate un sorbo de agua",
      "Cierra los ojos 10 segundos, descansa la vista",
      "Lo bueno toma su tiempo. Casi está"
    ],
    en: [
      "Take three deep breaths, drop your shoulders",
      "While we wait, stretch gently",
      "KORIVA is creating something unique for you",
      "Take a sip of water",
      "Close your eyes for 10 seconds, rest your view",
      "Good things take time. Almost there"
    ]
  }
  useEffect(() => {
    if (imgPhase !== "generating") { setWellnessIdx(0); return }
    const id = setInterval(() => setWellnessIdx(i => (i + 1) % wellnessMsgs.es.length), 5000)
    return () => clearInterval(id)
  }, [imgPhase])
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null)
  const [imgError, setImgError] = useState(null)
  const [editedPrompt, setEditedPrompt] = useState("")
  const [promptReal, setPromptReal] = useState("")
  const [regenCount, setRegenCount] = useState(0)
  const [imgApprovedDays, setImgApprovedDays] = useState({})
  const [imgUsed, setImgUsed]   = useState(0)
  const [imgTotal, setImgTotal] = useState(0)

  const productRef = useRef()
  const personRef  = useRef()
  const spaceRef   = useRef()

  // Space campaign
  const [spacePhoto, setSpacePhoto]   = useState(null)
  const [spaceType, setSpaceType]     = useState(null)
  const [spaceHighlight, setSpaceHighlight] = useState("")
  const [spaceAvoid, setSpaceAvoid]   = useState("")
  // Text image
  const [showTextImg, setShowTextImg] = useState(false)

  const es       = lang === "es"
  const focuses  = FOCUSES[lang]
  const days     = DAYS[lang]
  const msgs     = LOAD_MSGS[lang]
  const campTypes = CAMPAIGN_TYPES[lang]
  const focusObj = focuses.find(f => f.id === focus)
  const campObj  = campTypes.find(c => c.id === campaignType)
  const currentDay = result?.dias?.[activeDay]

  useEffect(() => {
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Manrope:wght@300;400;500;600&display=swap"
    document.head.appendChild(link)
  }, [])

  useEffect(() => {
    if (!showTextImg || !currentDay) return
    setTimeout(() => {
      const canvas = document.getElementById('text-img-canvas')
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const hook = currentDay.hook || ""
      const cta  = currentDay.cta  || ""
      const brand = project?.nombre.split(" ")[0].toUpperCase()
      ctx.fillStyle = "#0F172A"; ctx.fillRect(0,0,600,600)
      ctx.fillStyle = V; ctx.fillRect(0,0,600,7)
      ctx.fillStyle = V; ctx.fillRect(0,593,600,7)
      ctx.fillStyle = "#FFFFFF"
      ctx.font = "bold 44px Arial, sans-serif"
      ctx.textAlign = "center"
      const words = hook.split(' '), lines = []
      let line = ''
      for (let w of words) {
        const t = line + w + ' '
        if (ctx.measureText(t).width > 480 && line) { lines.push(line.trim()); line = w + ' ' }
        else line = t
      }
      lines.push(line.trim())
      const sy = 300 - (lines.length * 60)/2 + 20
      lines.forEach((l,i) => ctx.fillText(l, 300, sy + i*60))
      ctx.fillStyle = "#A78BFA"
      ctx.font = "500 20px Arial, sans-serif"
      // CTA tambien se parte en lineas si es largo (mismo metodo que el hook)
      const ctaWords = cta.split(' '), ctaLines = []
      let ctaLine = ''
      for (let w of ctaWords) {
        const t = ctaLine + w + ' '
        if (ctx.measureText(t).width > 520 && ctaLine) { ctaLines.push(ctaLine.trim()); ctaLine = w + ' ' }
        else ctaLine = t
      }
      ctaLines.push(ctaLine.trim())
      const ctaStartY = sy + lines.length*60 + 44
      ctaLines.forEach((l,i) => ctx.fillText(l, 300, ctaStartY + i*28))
      const afterCta = ctaStartY + ctaLines.length*28
      ctx.fillStyle = "rgba(255,255,255,0.2)"; ctx.fillRect(220, afterCta + 4, 160, 1)
      ctx.fillStyle = "rgba(255,255,255,0.3)"
      ctx.font = "bold 12px Arial, sans-serif"
      ctx.fillText(brand, 300, afterCta + 30)
    }, 100)
  }, [showTextImg, currentDay])

  useEffect(() => {
    if (phase !== "generating") return
    let i = 0
    const iv = setInterval(() => { i=(i+1)%msgs.length; setLoadMsg(i) }, 900)
    return () => clearInterval(iv)
  }, [phase, es])

  function handlePhoto(e, type) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      if (type === "product") { setProductPhoto(ev.target.result); analyzeProduct(ev.target.result) }
      else setPersonPhoto(ev.target.result)
    }
    reader.readAsDataURL(file)
  }

  async function cargarTemas() {
    setTemasLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(process.env.REACT_APP_API_URL + "/api/generate-week-topics", {
        method:"POST",
        headers:{ "Content-Type":"application/json", "Authorization": `Bearer ${session?.access_token}` },
        body: JSON.stringify({ project_id: project?.id })
      })
      const parsed = await res.json()
      if (res.ok) setTemas(parsed.temas || {})
      else alert(parsed.error || (es ? "No se pudieron generar los temas" : "Could not generate topics"))
    } catch (e) {
      alert(es ? "No se pudieron generar los temas" : "Could not generate topics")
    }
    setTemasLoading(false)
  }

  async function analyzeProduct(photoData) {
    setAnalyzingProduct(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(process.env.REACT_APP_API_URL + "/api/analyze-product", {
        method:"POST",
        headers:{ "Content-Type":"application/json", "Authorization": `Bearer ${session?.access_token}` },
        body: JSON.stringify({ photo_base64: photoData, idioma: es ? "es" : "en" })
      })
      const parsed = await res.json()
      if (res.ok) {
        setProductAnalysis(parsed)
        setProductName(parsed.nombre || "")
        setProductDesc(parsed.descripcion || "")
      } else {
        console.error("Error analizando producto:", parsed.error)
      }
    } catch (e) {
      console.error("No se pudo analizar el producto:", e)
    }
    setAnalyzingProduct(false)
  }
  async function generate() {
    if (phase === "generating") return  // candado: evita doble generación/guardado
    setPhase("generating"); setLoadMsg(0)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(process.env.REACT_APP_API_URL + "/api/generate-week", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + session.access_token
        },
        body: JSON.stringify({
          project_id: project.id,
          modo: mode,
          enfoque: focusObj?.label || focus || "",
          tema: tema.trim(),
          campaign_type: campObj?.label || campaignType || null,
          referencia_info: (
            mode === "product" && productName
              ? `Producto: "${productName}". ${productDesc ? "Descripción: " + productDesc + "." : ""} El usuario subió una foto de este producto para esta semana.`
              : mode === "person" && personRole
              ? `Persona: ${personRole}. ${personEmotion ? "Debe transmitir: " + personEmotion + "." : ""} El usuario subió una foto de esta persona para esta semana.`
              : mode === "space" && (spaceHighlight || spaceType)
              ? `Espacio: ${spaceType || ""}. ${spaceHighlight ? "Quiere destacar: " + spaceHighlight + "." : ""} ${spaceAvoid ? "Evitar mostrar: " + spaceAvoid + "." : ""} El usuario subió una foto de su espacio para esta semana.`
              : null
          )
        })
      })
      if (!res.ok) throw new Error("generate-week failed: " + res.status)
      const week = await res.json()
      const dias = (week.dias || []).map(day => ({
        ...day,
        dimensions: FORMAT_DIMS[day.formato] || FORMAT_DIMS["Post"],
        img_prompt_auto: day.img_prompt_auto || buildImgPrompt(day),
        img_approved: false,
      }))
      // Guardar la semana en Supabase (no frena al usuario si falla)
      let savedWeekId = null
      // Subir la foto de referencia a R2 segun el modo (producto/persona/espacio)
      let referencePhotoPath = null
      const fotoReferencia = mode === "product" ? productPhoto : mode === "person" ? personPhoto : mode === "space" ? spacePhoto : null
      console.log("FOTO REF: modo =", mode, "| hay foto?", !!fotoReferencia)
      if (fotoReferencia) {
        try {
          const { data: { session: sess } } = await supabase.auth.getSession()
          console.log("FOTO REF: subiendo foto al backend...")
          const upRes = await fetch(process.env.REACT_APP_API_URL + "/api/upload-reference-photo", {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${sess?.access_token}` },
            body: JSON.stringify({ photo_base64: fotoReferencia })
          })
          const upData = await upRes.json()
          console.log("FOTO REF: respuesta del backend:", upRes.status, upData)
          if (upRes.ok && upData.storage_path) referencePhotoPath = upData.storage_path
        } catch (upErr) {
          console.error("FOTO REF ERROR:", upErr)
        }
      }
      try {
        // Anti-duplicado: revisar si ya existe una semana idéntica creada hace muy poco
        const haceUnMinuto = new Date(Date.now() - 60000).toISOString()
        const { data: yaExiste } = await supabase
          .from("weeks")
          .select("id")
          .eq("project_id", project.id)
          .eq("tema", tema.trim())
          .eq("enfoque", focusObj?.label || focus || "")
          .gte("created_at", haceUnMinuto)
          .limit(1)
        if (yaExiste && yaExiste.length > 0) {
          savedWeekId = yaExiste[0].id
          // Si reusamos una semana existente, actualizarle la foto de referencia
          if (referencePhotoPath) {
            await supabase.from("weeks").update({ reference_photo_url: referencePhotoPath }).eq("id", savedWeekId)
            console.log("FOTO REF: actualizada en semana existente", savedWeekId)
          }
        } else {
          console.log("INSERT SEMANA: referencePhotoPath =", referencePhotoPath)
          const { data: savedWeek, error: saveErr } = await supabase
            .from("weeks")
            .insert({
              project_id: project.id,
              modo: mode,
              enfoque: focusObj?.label || focus || "",
              tema: tema.trim(),
              dias: dias,
              resumen_semana: week.resumen_semana || "",
              reference_photo_url: referencePhotoPath
            })
            .select("id")
            .single()
          console.log("INSERT SEMANA: error?", saveErr, "| guardada:", savedWeek)
          if (saveErr) {
            console.error("No se pudo guardar la semana en Supabase:", saveErr)
          } else {
            savedWeekId = savedWeek?.id || null
          }
        }
      } catch (saveCatch) {
        console.error("Error guardando la semana:", saveCatch)
      }

      // Marcar el tema sugerido como usado
      if (temaElegido && temas) {
        try {
          const nuevos = {}
          for (const k of Object.keys(temas)) {
            nuevos[k] = (temas[k]||[]).map(x => x.id===temaElegido ? {...x, usado:true} : x)
          }
          await supabase.from("projects").update({ temas_semana: nuevos }).eq("id", project.id)
          setTemas(nuevos)
          setTemaElegido(null)
        } catch (marcarErr) {
          console.error("No se pudo marcar el tema como usado:", marcarErr)
        }
      }

      setResult({ ...week, dias, week_id: savedWeekId })
      setActiveDay(0)
      setPhase("results")
    } catch (e) {
      console.error(e)
      setPhase(mode==="brand"?"focus":"product_setup")
    }
  }


  function buildImgPrompt(day) {
    const base = day.img_idea || day.img_concepto || ""
    const dims = (FORMAT_DIMS[day.formato] || FORMAT_DIMS["Post"]).size
    const productInfo = mode==="product" && productName ? `, con el producto "${productName}" en la escena de manera natural y destacada` : ""
    const personInfo  = mode==="person" && personRole ? `, incluyendo a ${personRole} en la escena transmitiendo ${personEmotion}` : ""
    return `${base}${productInfo}${personInfo}. Dimensiones: ${dims}. NUNCA incluir texto, letras ni palabras en la imagen.`
  }

  function openImgFlow(dayIdx) {
    setActiveDay(dayIdx)
    const day = result?.dias?.[dayIdx]
    if (day) {
      setEditedPrompt(day.img_idea || day.img_prompt_auto)
      setPromptReal(day.img_prompt_auto || day.img_idea)
    }
    setImgPhase("prompt")
    setRegenCount(0)
    setShowTextImg(false)
    setPhase("imgflow")
  }

  async function approveImage() {
    setImgError(null)
    setImgPhase("generating")
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(process.env.REACT_APP_API_URL + "/api/generate-image", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          prompt: promptReal || editedPrompt,
          estilo: result?.estilo_sugerido,
          formato: currentDay?.formato,
          project_id: project?.id,
          week_id: result?.week_id || null,
          character_id: personajeElegido || null,
          character_ids: personajesElegidos,
          incluir_logo: incluirLogo,
          icono_ai: iconoAI,
          texto_imagen: textoImagen,
          fuente_imagen: fuenteImagen,
          posicion_texto: posicionTexto,
          modo: mode
        })
      })
      const data = await res.json()
      if (!res.ok) {
        setImgError(data.error || "Error al generar la imagen")
        setImgPhase("prompt")
        return
      }
      setGeneratedImageUrl(data.image_url)
      // Calcular los dias actualizados ANTES (no depender de setResult)
      const diasActualizados = (result?.dias || []).map((d, i) =>
        i === activeDay
          ? { ...d, img_approved:true, img_prompt_final:editedPrompt, img_url:data.image_url }
          : d
      )
      setResult(prev => ({ ...prev, dias: diasActualizados }))
      // Guardar la imagen dentro de la semana en Supabase
      if (result?.week_id) {
        const { error: updErr } = await supabase.from("weeks").update({ dias: diasActualizados }).eq("id", result.week_id)
        if (updErr) console.error("Error guardando imagen en la semana:", updErr)
      }
      setImgUsed(u => u + (data.credits_used || 1))
      setImgPhase("final")
    } catch (e) {
      setImgError("No se pudo conectar con el servidor")
      setImgPhase("prompt")
    }
  }

  async function approveCarousel() {
    setImgError(null)
    setImgPhase("generating")
    try {
      const day = result?.dias?.[activeDay]
      let prompts = (day?.img_prompts_carrusel || []).slice(0, 3)
      // Plan B: si no vinieron los 3 conceptos del carrusel, los armamos con el concepto del dia
      if (prompts.length === 0) {
        const base = day?.img_idea || day?.img_prompt_auto || editedPrompt || ""
        if (base) {
          prompts = [
            `${base} — first slide of a 3-part visual sequence, same style and lighting`,
            `${base} — second slide of the same sequence, coherent style and palette`,
            `${base} — third slide closing the sequence, same visual identity`
          ]
        }
      }
      if (prompts.length === 0) {
        setImgError("No hay conceptos de carrusel para generar")
        setImgPhase("prompt")
        return
      }
      const { data: { session } } = await supabase.auth.getSession()

      // Generar las 3 imagenes UNA POR UNA (llamadas cortas que no se cuelgan)
      const urls = []
      let creditosUsados = 0
      for (const concepto of prompts) {
        const res = await fetch(process.env.REACT_APP_API_URL + "/api/generate-image", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            prompt: concepto,
            estilo: result?.estilo_sugerido,
            formato: currentDay?.formato,
            project_id: project?.id,
            week_id: result?.week_id || null,
            incluir_logo: incluirLogo,
            icono_ai: iconoAI,
            modo: mode
          })
        })
        const data = await res.json()
        if (!res.ok) {
          continue
        }
        urls.push(data.image_url)
        creditosUsados += (data.credits_used || 1)
      }

      if (urls.length === 0) {
        setImgError("No se pudo generar ninguna imagen del carrusel")
        setImgPhase("prompt")
        return
      }

      const diasCarrusel = (result?.dias || []).map((d, i) =>
        i === activeDay
          ? { ...d, img_approved:true, img_urls:urls }
          : d
      )
      setResult(prev => ({ ...prev, dias: diasCarrusel }))
      if (result?.week_id) {
        const { error: updErr } = await supabase.from("weeks").update({ dias: diasCarrusel }).eq("id", result.week_id)
        if (updErr) console.error("Error guardando carrusel en la semana:", updErr)
      }
      setImgUsed(u => u + creditosUsados)
      setImgPhase("final")
    } catch (e) {
      setImgError("No se pudo conectar con el servidor")
      setImgPhase("prompt")
    }
  }

  async function descargarImagen(url, nombre) {
    try {
      const r = await fetch(url, { mode: "cors", cache: "no-store" })
      if (!r.ok) throw new Error("fetch")
      const blob = await r.blob()
      const objUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = objUrl
      a.download = nombre || "koriva-imagen.png"
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(()=>URL.revokeObjectURL(objUrl), 4000)
    } catch (e) {
      window.open(url, "_blank")
    }
  }

  function copy(text, key) {
    navigator.clipboard.writeText(text).catch(()=>{})
    setCopied(key); setTimeout(()=>setCopied(null), 2200)
  }

  const T = {
    es: {
      modeLbl:"Generación semanal", modeTitle:"¿Qué tipo de contenido generamos esta semana?",
      modeSub:"Elige el enfoque principal. Esto define toda la estrategia de los próximos 7 días.",
      modeBrand:"Estrategia de marca", modeBrandDesc:"Contenido basado en tu marca, sin producto específico",
      modeProduct:"Campaña de producto", modeProductDesc:"Sube una foto de tu producto y KORIVA lo integra en toda la estrategia",
      modePerson:"Con una persona", modePersonDesc:"Tú, un cliente o modelo en las imágenes de la semana",
      modeSpace:"Mi espacio", modeSpaceDesc:"Sube una foto de tu local, consultorio, salón o estudio y KORIVA lo destaca en la estrategia",
      nextBtn:"Continuar →", backBtn:"← Volver",
      focusLbl:"Enfoque", focusTitle:"¿En qué enfocamos esta semana?",
      focusSub:"KORIVA usará la información guardada de tu marca y este enfoque para los próximos 7 días.",
      temaLbl:"Tema específico esta semana", temaPh:"Ej. Día de la madre, nuevo precio, testimonio... (opcional)",
      genBtn:"Generar estrategia semanal →", genNote:"Tu información de marca ya está guardada.",
      productTitle:"Tu producto de esta semana", productSub:"Sube una foto de tu producto. KORIVA lo analizará automáticamente.",
      uploadProduct:"Subir foto del producto",
      analyzingMsg:"Analizando tu producto...",
      productNameLbl:"Nombre del producto", productDescLbl:"Descripción del producto",
      editNote:"KORIVA analizó tu producto. Edita si algo no está correcto.",
      campTypeLbl:"Tipo de campaña",
      campContextLbl:"Contexto adicional",
      campContextPh:"Ej. Descuento del 20%, válido hasta el 15 de mayo, precio original $150",
      personTitle:"La persona de esta semana", personSub:"Sube una foto. KORIVA la integrará en las imágenes generadas.",
      uploadPerson:"Subir foto de la persona",
      personRoleLbl:"¿Quién es esta persona?", personRolePh:"Ej. La dueña del negocio, cliente satisfecho, modelo de la marca",
      personEmotionLbl:"¿Qué emoción o situación debe transmitir?", personEmotionPh:"Ej. Confianza y profesionalismo, celebración de logro, cotidianidad auténtica",
      personContextLbl:"Contexto adicional", personContextPh:"Ej. Vestuario sugerido, ambiente, qué no incluir",
      resLbl:"Semana generada", resTitle:"Tu estrategia está lista",
      stratLbl:"Estrategia", hook:"Hook", cta:"CTA", story:"Guión para video",
      hookHelp:"Es la frase de apertura que detiene el scroll. Cópiala como la primera línea del texto en tu post, carrusel o historia, o úsala como las primeras palabras de tu reel o video.",
      ctaHelp:"Es el llamado a la acción que cierra tu contenido. Cópialo al final del texto en tu post, carrusel o historia, o úsalo como las últimas palabras antes de cerrar tu reel o video.",
      storyIntro:"INTRO 5s", storyDev:"DESARROLLO 20s", storyClose:"CIERRE",
      copy:"Copiar", copied:"¡Copiado!",
      genImg:"Generar imagen →", imgApproved:"✓ Imagen lista",
      imgCounter:"imágenes disponibles",
      noCreditsTitle:"Te quedaste sin créditos de imagen",
      noCreditsSub:"Compra un paquete para seguir generando.",
      noCreditsBtn:"Comprar un paquete →",
      newWeek:"← Nueva semana",
      focusChipLbl:"Enfoque",
      videoNote:"Usa el guión como base para grabar. Tú grabas el video. Si prefieres no grabar, puedes generar la imagen de este día y usarla como historia, portada del reel, o armar el reel con una secuencia de imágenes.",
      noTextNote:"⚠️ KORIVA nunca incluye texto en las imágenes generadas. Agrega tu copy en Canva o Instagram.",
      imgFlowLbl:"Generador de imagen", imgFlowTitle:"Concepto de imagen",
      imgFlowSub:"Concepto generado automáticamente según tu estilo de marca, formato del día y enfoque semanal. Edítalo si quieres.",
      dimInfo:"Dimensiones automáticas",
      approveBtn:"Aprobar y generar imagen (usa 1 crédito) →",
      carouselBtn:"Generar carrusel (usa 3 créditos) →",
      carouselNote:"Este carrusel tiene 3 imágenes coherentes. Generarlas usa 3 créditos (solo se cobran las que se generen).",
      approveNote:"Ajustar el concepto es gratis. Cuando estés lista, generar la imagen usa 1 crédito de tu plan (Animado 3D: 2 créditos).",
      finalTitle:"Imagen generada y guardada",
      finalSub:"Tu imagen está guardada en tu biblioteca. Descárgala cuando quieras.",
      beforeAfterLbl:"Antes y después",
      beforeLbl:"Tu foto original", afterLbl:"Generada por KORIVA",
      regenBtn:"Regenerar (usa 1 crédito)", regenLeft:"regeneraciones disponibles",
      regenNote:"Regenerar usa otro crédito de tu plan (Animado 3D: 2 créditos).",
      saveBtn:"Guardar y volver al contenido →",
      backContent:"← Volver al contenido",
      productBadge:"Campaña de producto", personBadge:"Campaña con persona",
      spaceBadge:"Campaña con espacio",
      noPhotoYet:"Sube una foto para ver el antes y después",
      spaceTitle:"Tu espacio esta semana", spaceSub:"Sube una foto de tu local, consultorio, salón o estudio. KORIVA lo analiza y construye la estrategia alrededor de él.",
      uploadSpace:"Subir foto del espacio",
      spaceTypeLbl:"¿Qué tipo de espacio es?",
      spaceTypes:[{id:"local",label:"Local / Tienda"},{id:"consultorio",label:"Consultorio / Oficina"},{id:"salon",label:"Salón de belleza"},{id:"restaurante",label:"Restaurante / Café"},{id:"estudio",label:"Estudio creativo"},{id:"otro",label:"Otro"}],
      spaceHighlightLbl:"¿Qué quieres destacar esta semana?",
      spaceHighlightPh:"Ej. La remodelación nueva, el área de espera, la decoración navideña...",
      spaceAvoidLbl:"¿Algo que prefieres que no aparezca?",
      spaceAvoidPh:"Ej. El área de bodega, la zona de construcción... (opcional)",
      pixarBadge:"Animado 3D · 2 créditos por imagen",
      textImgTitle:"O genera una imagen de texto",
      textImgSub:"Sin IA. Sin créditos. Tu hook con el estilo y colores de tu marca — al instante.",
      textImgBtn:"Generar imagen de texto →",
      textImgNote:"Las imágenes de texto no consumen créditos. Se descargan directamente.",
      textImgDownload:"Descargar imagen de texto →",
    },
    en: {
      modeLbl:"Weekly generation", modeTitle:"What type of content do we generate this week?",
      modeSub:"Choose the main focus. This defines the entire strategy for the next 7 days.",
      modeBrand:"Brand strategy", modeBrandDesc:"Content based on your brand, no specific product",
      modeProduct:"Product campaign", modeProductDesc:"Upload a product photo and KORIVA integrates it into the full strategy",
      modePerson:"With a person", modePersonDesc:"You, a client or model in this week's images",
      modeSpace:"My space", modeSpaceDesc:"Upload a photo of your location, office, salon or studio and KORIVA highlights it in the strategy",
      nextBtn:"Continue →", backBtn:"← Back",
      focusLbl:"Focus", focusTitle:"What's our focus this week?",
      focusSub:"KORIVA will use your saved brand information and this focus for the next 7 days.",
      temaLbl:"Specific topic this week", temaPh:"E.g. Mother's day, new price, testimonial... (optional)",
      genBtn:"Generate weekly strategy →", genNote:"Your brand information is already saved.",
      productTitle:"Your product this week", productSub:"Upload a product photo. KORIVA will analyze it automatically.",
      uploadProduct:"Upload product photo",
      analyzingMsg:"Analyzing your product...",
      productNameLbl:"Product name", productDescLbl:"Product description",
      editNote:"KORIVA analyzed your product. Edit if anything is incorrect.",
      campTypeLbl:"Campaign type",
      campContextLbl:"Additional context",
      campContextPh:"E.g. 20% discount, valid until May 15, original price $150",
      personTitle:"The person this week", personSub:"Upload a photo. KORIVA will integrate them into generated images.",
      uploadPerson:"Upload person photo",
      personRoleLbl:"Who is this person?", personRolePh:"E.g. Business owner, satisfied client, brand model",
      personEmotionLbl:"What emotion or situation should they convey?", personEmotionPh:"E.g. Confidence and professionalism, achievement celebration, authentic everyday moment",
      personContextLbl:"Additional context", personContextPh:"E.g. Suggested outfit, environment, what not to include",
      resLbl:"Week generated", resTitle:"Your strategy is ready",
      stratLbl:"Strategy", hook:"Hook", cta:"CTA", story:"Video script",
      hookHelp:"It's the opening line that stops the scroll. Copy it as the first line of the text in your post, carousel or story, or use it as the first words of your reel or video.",
      ctaHelp:"It's the call to action that closes your content. Copy it at the end of the text in your post, carousel or story, or use it as the last words before closing your reel or video.",
      storyIntro:"INTRO 5s", storyDev:"DEVELOPMENT 20s", storyClose:"CLOSING",
      copy:"Copy", copied:"Copied!",
      genImg:"Generate image →", imgApproved:"✓ Image ready",
      imgCounter:"images available",
      noCreditsTitle:"You're out of image credits",
      noCreditsSub:"Buy a pack to keep generating.",
      noCreditsBtn:"Buy a pack →",
      newWeek:"← New week",
      focusChipLbl:"Focus",
      videoNote:"Use the script as a base to record. You record the video. If you prefer not to record, you can generate this day's image and use it as a story, a reel cover, or build the reel from a sequence of images.",
      noTextNote:"⚠️ KORIVA never includes text in generated images. Add your copy in Canva or Instagram.",
      imgFlowLbl:"Image generator", imgFlowTitle:"Image concept",
      imgFlowSub:"Automatically generated based on your brand style, day format and weekly focus. Edit if needed.",
      dimInfo:"Automatic dimensions",
      approveBtn:"Approve and generate image (uses 1 credit) →",
      carouselBtn:"Generate carousel (uses 3 credits) →",
      carouselNote:"This carousel has 3 coherent images. Generating them uses 3 credits (you are only charged for the ones generated).",
      approveNote:"Adjusting the concept is free. When you're ready, generating the image uses 1 credit from your plan (3D Animated: 2 credits).",
      finalTitle:"Image generated and saved",
      finalSub:"Your image is saved in your library. Download it whenever you want.",
      beforeAfterLbl:"Before & after",
      beforeLbl:"Your original photo", afterLbl:"Generated by KORIVA",
      regenBtn:"Regenerate (uses 1 credit)", regenLeft:"regenerations available",
      regenNote:"Regenerating uses another credit from your plan (3D Animated: 2 credits).",
      saveBtn:"Save and return to content →",
      backContent:"← Back to content",
      productBadge:"Product campaign", personBadge:"Person campaign",
      spaceBadge:"Space campaign",
      noPhotoYet:"Upload a photo to see before & after",
      spaceTitle:"Your space this week", spaceSub:"Upload a photo of your location, office, salon or studio. KORIVA analyzes it and builds the strategy around it.",
      uploadSpace:"Upload space photo",
      spaceTypeLbl:"What type of space is it?",
      spaceTypes:[{id:"local",label:"Store / Shop"},{id:"consultorio",label:"Office / Clinic"},{id:"salon",label:"Beauty salon"},{id:"restaurante",label:"Restaurant / Café"},{id:"estudio",label:"Creative studio"},{id:"otro",label:"Other"}],
      spaceHighlightLbl:"What do you want to highlight this week?",
      spaceHighlightPh:"E.g. New renovation, waiting area, holiday decorations...",
      spaceAvoidLbl:"Anything you prefer not to appear?",
      spaceAvoidPh:"E.g. Storage area, construction zone... (optional)",
      pixarBadge:"Animado 3D · 2 credits per image",
      textImgTitle:"Or generate a text image",
      textImgSub:"No AI. No credits. Your hook with your brand style and colors — instantly.",
      textImgBtn:"Generate text image →",
      textImgNote:"Text images use no credits. Download directly.",
      textImgDownload:"Download text image →",
    }
  }
  const t = T[lang]

  const s = {
    page:       { minHeight:"100vh", background:WHITE, fontFamily:B, color:TEXT },
    header:     { borderBottom:`1px solid ${BORDER}`, padding:".85rem 2rem", display:"flex", alignItems:"center", justifyContent:"space-between" },
    logo:       { fontFamily:H, fontSize:17, fontWeight:800, color:TEXT, letterSpacing:"-.02em" },
    logoV:      { color:V },
    hRight:     { display:"flex", alignItems:"center", gap:"1rem" },
    brandChip:  { display:"flex", alignItems:"center", gap:6, background:OFF, border:`1px solid ${BORDER}`, borderRadius:20, padding:"4px 12px" },
    brandDot:   { width:6, height:6, background:SUCCESS, borderRadius:"50%" },
    brandName:  { fontSize:12, fontWeight:600, color:TEXT },
    langRow:    { display:"flex", gap:3, background:OFF, borderRadius:8, padding:3, border:`1px solid ${BORDER}` },
    langBtn:    (a) => ({ background:a?V:"transparent", color:a?WHITE:MUTED, border:"none", borderRadius:6, padding:"4px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:B, transition:"all .18s" }),
    wrap:       { maxWidth:1000, margin:"0 auto", padding:"2.5rem 1.5rem" },
    topLbl:     { fontSize:12, fontWeight:700, letterSpacing:".14em", color:V, textTransform:"uppercase", marginBottom:".5rem", fontFamily:B },
    pageTitle:  { fontFamily:H, fontSize:34, fontWeight:800, color:TEXT, marginBottom:".5rem", letterSpacing:"-.03em", lineHeight:1.1 },
    pageSub:    { fontSize:16, color:MUTED, lineHeight:1.65, marginBottom:"2rem", fontWeight:400 },
    modeGrid:   { display:"grid", gridTemplateColumns:"1fr", gap:10, marginBottom:"1.5rem" },
    modeCard:   (a) => ({ border:a?`2px solid ${V}`:`1.5px solid ${BORDER}`, background:a?V_SOFT:WHITE, borderRadius:12, padding:"1rem 1.1rem 1rem 1.3rem", cursor:"pointer", transition:"all .18s", position:"relative", overflow:"hidden", display:"flex", alignItems:"center", gap:"1rem" }),
    modeLine:   (a) => ({ position:"absolute", left:0, top:0, bottom:0, width:3, background:a?V:BORDER }),
    modeIcon:   { fontSize:28, flexShrink:0 },
    modeName:   (a) => ({ fontFamily:H, fontSize:15, fontWeight:700, color:a?V:TEXT, marginBottom:2, letterSpacing:"-.01em" }),
    modeDesc:   { fontSize:12, color:MUTED, fontWeight:400 },
    focusGrid:  { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:"1.5rem" },
    focusCard:  (a) => ({ border:a?`2px solid ${V}`:`1.5px solid ${BORDER}`, background:a?V_SOFT:WHITE, borderRadius:10, padding:".8rem 1rem .8rem 1.1rem", cursor:"pointer", transition:"all .18s", position:"relative", overflow:"hidden" }),
    focusLine:  (a) => ({ position:"absolute", left:0, top:0, bottom:0, width:3, background:a?V:BORDER }),
    focusName:  (a) => ({ fontFamily:H, fontSize:12, fontWeight:700, color:a?V:TEXT, marginBottom:2 }),
    focusDesc:  { fontSize:11, color:MUTED, fontWeight:400 },
    fieldLbl:   { fontSize:12, fontWeight:600, color:TEXT, marginBottom:5, display:"block", fontFamily:B },
    textarea:   { width:"100%", border:`1.5px solid ${BORDER}`, background:WHITE, borderRadius:8, padding:"10px 13px", fontFamily:B, fontSize:13, color:TEXT, outline:"none", resize:"none", lineHeight:1.6, boxSizing:"border-box", transition:"border .2s" },
    input:      { width:"100%", border:`1.5px solid ${BORDER}`, background:WHITE, borderRadius:8, padding:"10px 13px", fontFamily:B, fontSize:13, color:TEXT, outline:"none", boxSizing:"border-box", transition:"border .2s", marginBottom:10 },
    uploadBox:  (hasPhoto) => ({ border:`1.5px dashed ${hasPhoto?SUCCESS:BORDER}`, background:hasPhoto?SUCCESS_BG:OFF, borderRadius:12, padding:"1.25rem", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:".6rem", cursor:"pointer", marginBottom:"1rem", minHeight:100, transition:"all .2s" }),
    uploadBtn:  { background:V_SOFT, color:V, border:`1px solid ${V_MID}`, borderRadius:7, padding:"8px 16px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:B },
    photoPreview:{ width:"100%", maxHeight:200, objectFit:"contain", borderRadius:8, marginBottom:8, background:"#F5F5F7" },
    analyzeBox: { background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:10, padding:".75rem 1rem", marginBottom:"1rem", display:"flex", alignItems:"center", gap:8 },
    analyzeSpinner:{ width:14, height:14, border:`2px solid ${V_MID}`, borderTop:`2px solid ${V}`, borderRadius:"50%", animation:"spin .8s linear infinite", flexShrink:0 },
    analyzeEditNote:{ fontSize:12, color:MUTED, fontStyle:"italic", marginBottom:8 },
    campGrid:   { display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:"1.25rem" },
    campCard:   (a) => ({ border:a?`2px solid ${V}`:`1.5px solid ${BORDER}`, background:a?V_SOFT:WHITE, borderRadius:10, padding:".7rem .85rem", cursor:"pointer", transition:"all .18s" }),
    campIcon:   { fontSize:16, display:"block", marginBottom:3 },
    campName:   (a) => ({ fontFamily:H, fontSize:11, fontWeight:700, color:a?V:TEXT, marginBottom:1 }),
    campDesc:   { fontSize:10, color:MUTED },
    btnPrimary: (dis) => ({ padding:"13px 24px", background:dis?BORDER:V, color:dis?MUTED:WHITE, border:"none", borderRadius:8, fontFamily:H, fontSize:14, fontWeight:700, cursor:dis?"not-allowed":"pointer", letterSpacing:"-.01em", transition:"all .2s" }),
    btnSecond:  { padding:"11px 18px", background:"transparent", color:MUTED, border:`1.5px solid ${BORDER}`, borderRadius:8, fontFamily:B, fontSize:13, fontWeight:500, cursor:"pointer" },
    navRow:     { display:"flex", gap:10, marginTop:"1.5rem", alignItems:"center" },
    loadWrap:   { minHeight:"65vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"1.25rem" },
    spinner:    { width:48, height:48, border:`3px solid ${BORDER}`, borderTop:`3px solid ${V}`, borderRadius:"50%", animation:"spin 1s linear infinite" },
    loadBrand:  { fontSize:12, color:V, fontWeight:700 },
    loadTxt:    { fontSize:13, color:MUTED },
    stratBox:   { background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:10, padding:".85rem 1rem", marginBottom:"1.25rem", display:"flex", gap:8 },
    stratDot:   { width:7, height:7, background:V, borderRadius:"50%", flexShrink:0, marginTop:5 },
    stratTxt:   { fontSize:16, color:TEXT, lineHeight:1.7, fontWeight:400 },
    chips:      { display:"flex", gap:6, marginBottom:"1.25rem", flexWrap:"wrap" },
    chip:       (color) => ({ display:"inline-flex", alignItems:"center", gap:4, background:color==="v"?V_SOFT:"#FFF0F6", border:`1px solid ${color==="v"?V_MID:"#FFD6EB"}`, borderRadius:20, padding:"3px 10px", fontSize:10, color:color==="v"?V:"#BE185D", fontWeight:700, fontFamily:B, textTransform:"uppercase" }),
    dayTabs:    { display:"flex", gap:8, marginBottom:"1.5rem", justifyContent:"space-between", overflowX:"auto", paddingBottom:2 },
    dayTab:     (a) => ({ padding:"12px 24px", borderRadius:24, border:a?`2px solid ${V}`:`2px solid ${BORDER}`, background:a?V:WHITE, color:a?WHITE:MUTED, fontSize:15, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap", fontFamily:B, transition:"all .18s", flexShrink:0 }),
    dayCard:    { background:OFF, border:`1px solid ${BORDER}`, borderRadius:13, overflow:"hidden", marginBottom:"1rem" },
    dayHeader:  { background:WHITE, borderBottom:`1px solid ${BORDER}`, padding:".65rem 1.1rem", display:"flex", alignItems:"center", justifyContent:"space-between" },
    dayNum:     { fontFamily:H, fontSize:16, fontWeight:800, color:TEXT, letterSpacing:"-.01em" },
    dayTema:    { fontSize:12, color:MUTED },
    dayRight:   { display:"flex", alignItems:"center", gap:6 },
    fmtBadge:   (fmt) => { const c=FORMAT_COLORS[fmt]||{bg:OFF,color:MUTED}; return {background:c.bg,color:c.color,fontSize:11,fontWeight:700,letterSpacing:".08em",padding:"3px 10px",borderRadius:20,textTransform:"uppercase",fontFamily:B} },
    dimBadge:   { fontSize:11, color:MUTED, fontFamily:B, background:OFF, border:`1px solid ${BORDER}`, borderRadius:10, padding:"3px 10px" },
    dayBody:    { padding:"1.25rem 1.5rem", display:"grid", gridTemplateColumns:"1fr 220px", gap:"1.75rem", alignItems:"start" },
    fieldBlock: { marginBottom:".65rem" },
    miniLbl:    { fontSize:11, fontWeight:700, letterSpacing:".12em", color:V, textTransform:"uppercase", marginBottom:".4rem", fontFamily:B },
    contentRow: { display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 },
    contentTxt: { fontSize:18, color:TEXT, lineHeight:1.7, fontWeight:400, flex:1 },
    ctaTxt:     { fontSize:18, color:TEXT, lineHeight:1.7, fontWeight:500, flex:1 },
    copyBtn:    (a) => ({ background:a?V:"transparent", color:a?WHITE:MUTED, border:`1px solid ${a?V:BORDER}`, borderRadius:6, padding:"5px 11px", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:B, transition:"all .18s", whiteSpace:"nowrap", flexShrink:0 }),
    divLine:    { height:1, background:BORDER, margin:".55rem 0" },
    storyBox:   { background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:9, padding:".75rem .9rem", marginTop:4 },
    storyTitle: { fontSize:9, fontWeight:700, color:"#92400E", letterSpacing:".1em", textTransform:"uppercase", marginBottom:".5rem", fontFamily:B },
    storyPart:  { marginBottom:".4rem" },
    storyLbl:   { fontSize:9, fontWeight:700, color:"#B45309", fontFamily:B },
    storyTxt:   { fontSize:12, color:"#78350F", lineHeight:1.6 },
    videoNote:  { marginTop:5, fontSize:10, color:MUTED, background:WHITE, border:`1px solid ${BORDER}`, borderRadius:5, padding:"4px 8px", fontStyle:"italic" },
    imgCol:     {},
    imgColLbl:  { fontSize:11, color:MUTED, fontWeight:700, textTransform:"uppercase", letterSpacing:".08em", marginBottom:6, fontFamily:B },
    imgGenBtn:  (approved) => ({ width:"100%", padding:"9px 0", background:approved?SUCCESS_BG:V_SOFT, color:approved?SUCCESS:V, border:`1px solid ${approved?SUCCESS:V_MID}`, borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:B, marginTop:6 }),
    divider:    { height:1, background:BORDER, margin:"1.25rem 0" },
    counterRow: { display:"flex", alignItems:"center", justifyContent:"space-between", background:OFF, border:`1px solid ${BORDER}`, borderRadius:9, padding:".65rem 1rem", marginBottom:"1rem" },
    counterLbl: { fontSize:15, color:MUTED },
    counterNum: { fontFamily:H, fontSize:18, fontWeight:800, color:V },
    newWeekBtn: { background:"none", border:"none", color:MUTED, fontSize:15, cursor:"pointer", fontFamily:B, padding:".4rem 0", display:"flex", alignItems:"center", gap:4 },
    // IMG FLOW
    autoBox:    { background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:10, padding:".85rem 1rem", marginBottom:"1rem" },
    autoLbl:    { fontSize:11, fontWeight:700, letterSpacing:".12em", color:V, textTransform:"uppercase", marginBottom:".6rem", fontFamily:B },
    autoTxt:    { fontSize:16, color:TEXT, lineHeight:1.7, fontWeight:400 },
    dimInfo:    { display:"flex", alignItems:"center", gap:6, marginTop:".6rem", fontSize:11, color:V, fontWeight:600, fontFamily:B },
    editArea:   { width:"100%", border:`1.5px solid ${V_MID}`, background:WHITE, borderRadius:8, padding:"12px 16px", fontFamily:B, fontSize:15, color:TEXT, outline:"none", resize:"vertical", minHeight:110, lineHeight:1.7, boxSizing:"border-box", marginBottom:".75rem" },
    noTextBox:  { background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:10, padding:"12px 16px", fontSize:13, color:TEXT, marginBottom:"1rem", fontFamily:B, lineHeight:1.6 },
    approveBtn: { width:"100%", padding:"13px", background:V, color:WHITE, border:"none", borderRadius:9, fontFamily:H, fontSize:14, fontWeight:700, cursor:"pointer", letterSpacing:"-.01em", marginBottom:".75rem" },
    backBtn:    { background:"none", border:"none", color:MUTED, fontSize:15, cursor:"pointer", fontFamily:B, padding:".3rem 0" },
    // BEFORE AFTER
    baGrid:     { display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:"1rem" },
    baCard:     { borderRadius:10, overflow:"hidden", border:`1px solid ${BORDER}` },
    baLbl:      { fontSize:10, fontWeight:700, color:MUTED, padding:"6px 10px", background:OFF, borderBottom:`1px solid ${BORDER}`, fontFamily:B },
    baImg:      { width:"100%", height:200, objectFit:"contain", display:"block", background:"#F5F5F7" },
    regenRow:   { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:".75rem" },
    regenDots:  { display:"flex", gap:5, alignItems:"center" },
    regenDot:   (used) => ({ width:8, height:8, borderRadius:"50%", background:used?V:BORDER, transition:"background .2s" }),
    regenInfo:  { fontSize:11, color:MUTED, marginLeft:6 },
    regenBtn:   (dis) => ({ padding:"8px 16px", background:"transparent", border:`1.5px solid ${dis?BORDER:BORDER}`, color:dis?BORDER:MUTED, borderRadius:7, fontFamily:B, fontSize:12, fontWeight:600, cursor:dis?"not-allowed":"pointer", transition:"all .2s", display:"flex", alignItems:"center", gap:5 }),
    saveBtn:    { width:"100%", padding:"13px", background:SUCCESS, color:WHITE, border:"none", borderRadius:9, fontFamily:H, fontSize:13, fontWeight:700, cursor:"pointer" },
  }

  const canGoGenerate = mode==="brand"
    ? !!focus
    : mode==="product"
    ? !!productName && !!campaignType && !!focus
    : mode==="person"
    ? !!personRole && !!personEmotion && !!focus
    : mode==="space"
    ? !!spacePhoto && !!spaceType && !!focus
    : false

  return (
    <div style={s.page}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fi{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        .fi{animation:fi .35s ease}
        textarea:focus,input:focus{border-color:${V}!important}
        ::-webkit-scrollbar{height:4px;width:4px}
        ::-webkit-scrollbar-thumb{background:${BORDER};border-radius:2px}
      `}</style>

      <div style={s.header}>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={s.logo}>KORIVA <span style={s.logoV}>Studio™</span></div>
          <button onClick={()=>navigate("/dashboard")}
            style={{ background:"none", border:"none", color:V, fontFamily:B, fontSize:13, fontWeight:700, cursor:"pointer", padding:"4px 0", display:"flex", alignItems:"center", gap:5 }}>
            <span>←</span><span>{es ? "Mis proyectos" : "My projects"}</span>
          </button>
        </div>
        <div style={s.hRight}>
          <div style={s.brandChip}><div style={s.brandDot}/><span style={s.brandName}>{project?.nombre}</span></div>
          <div style={s.langRow}>
            <button style={s.langBtn(es)} onClick={()=>setLang("es")}>ES</button>
            <button style={s.langBtn(!es)} onClick={()=>setLang("en")}>EN</button>
          </div>
        </div>
      </div>

      <div style={s.wrap}>

        {/* ── MODE SELECTION ── */}
        {phase==="ya_tiene_semana" && (
          <div className="fi">
            <div style={s.topLbl}>{es?"TU SEMANA":"YOUR WEEK"}</div>
            <div style={s.pageTitle}>{es?"Ya tienes tu estrategia de esta semana":"You already have this week's strategy"}</div>
            <p style={s.pageSub}>{es?"Tu plan incluye una estrategia por semana. La que creaste está guardada en tu archive, lista para trabajar.":"Your plan includes one strategy per week. The one you created is saved in your archive, ready to work on."}</p>
            <div style={s.navRow}>
              <button style={s.btnPrimary(false)} onClick={()=>navigate(semanaActualId ? `/archive` : `/archive`)}>
                {es?"Ver mi semana en el archive":"View my week in the archive"}
              </button>
            </div>
          </div>
        )}

        {phase==="mode" && (
          <div className="fi">
            <div style={s.topLbl}>{t.modeLbl}</div>
            <div style={s.pageTitle}>{t.modeTitle}</div>
            <p style={s.pageSub}>{t.modeSub}</p>
            <div style={s.modeGrid}>
              {[
                {id:"brand",   name:t.modeBrand,   desc:t.modeBrandDesc},
                {id:"product", name:t.modeProduct, desc:t.modeProductDesc},
                {id:"space",   name:t.modeSpace,   desc:t.modeSpaceDesc},
                {id:"person",  name:t.modePerson,  desc:t.modePersonDesc},
              ].map(m=>(
                <div key={m.id} style={s.modeCard(mode===m.id)} onClick={()=>setMode(m.id)}>
                  <div style={s.modeLine(mode===m.id)}/>
                  <div>
                    <div style={s.modeName(mode===m.id)}>{m.name}</div>
                    <div style={s.modeDesc}>{m.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={s.navRow}>
              <button style={s.btnPrimary(!mode)} disabled={!mode}
                onClick={()=>setPhase(mode==="brand"?"focus":mode==="product"?"product_setup":mode==="space"?"space_setup":"person_setup")}>
                {t.nextBtn}
              </button>
            </div>
          </div>
        )}

        {/* ── PRODUCT SETUP ── */}
        {phase==="product_setup" && (
          <div className="fi">
            <div style={s.topLbl}>{t.modeProduct}</div>
            <div style={s.pageTitle}>{t.productTitle}</div>
            <p style={s.pageSub}>{t.productSub}</p>

            <div style={s.uploadBox(!!productPhoto)} onClick={()=>productRef.current.click()}>
              {productPhoto
                ? <img src={productPhoto} alt="product" style={s.photoPreview}/>
                : <><button style={s.uploadBtn}>{t.uploadProduct}</button></>
              }
              <input ref={productRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>handlePhoto(e,"product")}/>
            </div>

            {analyzingProduct && (
              <div style={s.analyzeBox}>
                <div style={s.analyzeSpinner}/>
                <span style={{fontSize:12,color:V,fontFamily:B}}>{t.analyzingMsg}</span>
              </div>
            )}

            {productAnalysis && !analyzingProduct && (
              <div>
                <div style={s.analyzeEditNote}>{t.editNote}</div>
                <label style={s.fieldLbl}>{t.productNameLbl}</label>
                <input style={s.input} value={productName} onChange={e=>setProductName(e.target.value)} placeholder="Ej. Jabón artesanal de lavanda"/>
                <label style={s.fieldLbl}>{t.productDescLbl}</label>
                <textarea style={{...s.textarea,height:60,marginBottom:16}} value={productDesc} onChange={e=>setProductDesc(e.target.value)}/>
              </div>
            )}

            {productPhoto && !analyzingProduct && (
              <>
                <label style={{...s.fieldLbl,marginBottom:8}}>{t.campTypeLbl}</label>
                <div style={s.campGrid}>
                  {campTypes.map(c=>(
                    <div key={c.id} style={s.campCard(campaignType===c.id)} onClick={()=>setCampaignType(c.id)}>
                      <div style={s.campName(campaignType===c.id)}>{c.label}</div>
                      <div style={s.campDesc}>{c.desc}</div>
                    </div>
                  ))}
                </div>

                <label style={s.fieldLbl}>{t.campContextLbl}</label>
                <textarea style={{...s.textarea,height:60,marginBottom:16}} value={campaignContext} onChange={e=>setCampaignContext(e.target.value)} placeholder={t.campContextPh}/>
              </>
            )}

            <div style={s.navRow}>
              <button style={s.btnSecond} onClick={()=>setPhase("mode")}>{t.backBtn}</button>
              <button style={s.btnPrimary(!productPhoto||!campaignType)} disabled={!productPhoto||!campaignType}
                onClick={()=>setPhase("focus")}>{t.nextBtn}</button>
            </div>
          </div>
        )}

        {/* ── PERSON SETUP ── */}
        {phase==="person_setup" && (
          <div className="fi">
            <div style={s.topLbl}>{t.modePerson}</div>
            <div style={s.pageTitle}>{t.personTitle}</div>
            <p style={s.pageSub}>{t.personSub}</p>

            <div style={s.uploadBox(!!personPhoto)} onClick={()=>personRef.current.click()}>
              {personPhoto
                ? <img src={personPhoto} alt="person" style={s.photoPreview}/>
                : <><button style={s.uploadBtn}>{t.uploadPerson}</button></>
              }
              <input ref={personRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>handlePhoto(e,"person")}/>
            </div>

            <label style={s.fieldLbl}>{t.personRoleLbl}</label>
            <input style={s.input} value={personRole} onChange={e=>setPersonRole(e.target.value)} placeholder={t.personRolePh}/>
            <label style={s.fieldLbl}>{t.personEmotionLbl}</label>
            <input style={s.input} value={personEmotion} onChange={e=>setPersonEmotion(e.target.value)} placeholder={t.personEmotionPh}/>
            <label style={s.fieldLbl}>{t.personContextLbl}</label>
            <textarea style={{...s.textarea,height:56,marginBottom:16}} value={personContext} onChange={e=>setPersonContext(e.target.value)} placeholder={t.personContextPh}/>

            <div style={s.navRow}>
              <button style={s.btnSecond} onClick={()=>setPhase("mode")}>{t.backBtn}</button>
              <button style={s.btnPrimary(!personRole||!personEmotion)} disabled={!personRole||!personEmotion}
                onClick={()=>setPhase("focus")}>{t.nextBtn}</button>
            </div>
          </div>
        )}

        {/* ── SPACE SETUP ── */}
        {phase==="space_setup" && (
          <div className="fi">
            <div style={s.topLbl}>{t.modeSpace}</div>
            <div style={s.pageTitle}>{t.spaceTitle}</div>
            <p style={s.pageSub}>{t.spaceSub}</p>

            <div style={s.uploadBox(!!spacePhoto)} onClick={()=>spaceRef.current.click()}>
              {spacePhoto
                ? <img src={spacePhoto} alt="space" style={s.photoPreview}/>
                : <><button style={s.uploadBtn}>{t.uploadSpace}</button></>
              }
              <input ref={spaceRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
                const f=e.target.files[0]; if(!f)return
                const r=new FileReader(); r.onload=ev=>setSpacePhoto(ev.target.result); r.readAsDataURL(f)
              }}/>
            </div>

            <label style={{...s.fieldLbl,marginBottom:8}}>{t.spaceTypeLbl}</label>
            <div style={s.campGrid}>
              {t.spaceTypes.map(st=>(
                <div key={st.id} style={s.campCard(spaceType===st.id)} onClick={()=>setSpaceType(st.id)}>
                  <div style={s.campName(spaceType===st.id)}>{st.label}</div>
                </div>
              ))}
            </div>

            <label style={s.fieldLbl}>{t.spaceHighlightLbl}</label>
            <textarea style={{...s.textarea,height:60,marginBottom:12}} value={spaceHighlight} onChange={e=>setSpaceHighlight(e.target.value)} placeholder={t.spaceHighlightPh}/>

            <label style={s.fieldLbl}>{t.spaceAvoidLbl}</label>
            <textarea style={{...s.textarea,height:52,marginBottom:16}} value={spaceAvoid} onChange={e=>setSpaceAvoid(e.target.value)} placeholder={t.spaceAvoidPh}/>

            <div style={s.navRow}>
              <button style={s.btnSecond} onClick={()=>setPhase("mode")}>{t.backBtn}</button>
              <button style={s.btnPrimary(!spacePhoto||!spaceType)} disabled={!spacePhoto||!spaceType}
                onClick={()=>setPhase("focus")}>{t.nextBtn}</button>
            </div>
          </div>
        )}

        {/* ── FOCUS ── */}
        {phase==="focus" && (
          <div className="fi">
            <div style={s.topLbl}>{t.focusLbl}</div>
            <div style={s.pageTitle}>{t.focusTitle}</div>
            <p style={s.pageSub}>{t.focusSub}</p>

            <div style={s.focusGrid}>
              {focuses.map(f=>(
                <div key={f.id} style={s.focusCard(focus===f.id)} onClick={()=>setFocus(f.id)}>
                  <div style={s.focusLine(focus===f.id)}/>
                  <div style={s.focusName(focus===f.id)}>{f.label}</div>
                  <div style={s.focusDesc}>{f.desc}</div>
                </div>
              ))}
            </div>

            {focus && (
              <div style={{marginBottom:"1.5rem"}}>
                {!temas && (
                  <>
                  <div style={{fontFamily:B,fontSize:14,color:MUTED,marginBottom:".9rem",lineHeight:1.6}}>
                    {es
                      ? "Explora los temas que pensamos para tu marca. Pueden darte una estrategia más fuerte que empezar de cero."
                      : "Explore the topics we wrote for your brand. They can give you a stronger strategy than starting from scratch."}
                  </div>
                  <button
                    style={{
                      width:"100%", background:V, color:WHITE, border:"none",
                      borderRadius:10, padding:".9rem 1rem", cursor:"pointer",
                      fontFamily:H, fontWeight:800, fontSize:15
                    }}
                    disabled={temasLoading}
                    onClick={cargarTemas}>
                    {temasLoading
                      ? (es?"Pensando temas para tu marca...":"Thinking of topics for your brand...")
                      : (es?"Ver temas sugeridos para tu marca":"See suggested topics for your brand")}
                  </button>
                  </>
                )}

                {temas && (temas[focus]||[]).filter(x=>!x.usado).length>0 && (
                  <>
                    <div style={{fontSize:12,color:MUTED,marginBottom:".6rem"}}>
                      {es
                        ? "Los pensamos con la informacion de tu marca. Marca uno para usarlo."
                        : "We wrote these from your brand information. Pick one to use it."}
                    </div>
                    {(temas[focus]||[]).filter(x=>!x.usado).map(x=>(
                      <div key={x.id}
                        onClick={()=>{
                          if (temaElegido===x.id) { setTemaElegido(null); setTema("") }
                          else { setTemaElegido(x.id); setTema(x.tema) }
                        }}
                        style={{
                          border:`1px solid ${temaElegido===x.id?V:BORDER}`,
                          background: temaElegido===x.id?V_SOFT:WHITE,
                          borderRadius:10, padding:".8rem 1rem", marginBottom:".5rem", cursor:"pointer"
                        }}>
                        <div style={{display:"flex",alignItems:"flex-start",gap:".7rem"}}>
                          <div style={{
                            width:16,height:16,flexShrink:0,marginTop:2,borderRadius:4,
                            border:`1.5px solid ${temaElegido===x.id?V:"#C9C9C9"}`,
                            background: temaElegido===x.id?V:"transparent"
                          }}/>
                          <div>
                            <div style={{fontFamily:H,fontWeight:800,fontSize:14,color:TEXT,marginBottom:".2rem"}}>{x.nombre}</div>
                            <div style={{fontFamily:B,fontSize:13,color:MUTED,lineHeight:1.5}}>{x.tema}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            <label style={{...s.fieldLbl,marginBottom:".2rem"}}>{t.temaLbl}</label>
            <textarea style={{...s.textarea,height:52,marginBottom:"1.5rem"}} value={tema} onChange={e=>setTema(e.target.value)} placeholder={t.temaPh}/>

            <div style={s.navRow}>
              <button style={s.btnSecond} onClick={()=>setPhase(mode==="brand"?"mode":mode==="product"?"product_setup":"person_setup")}>{t.backBtn}</button>
              <button style={s.btnPrimary(!focus)} disabled={!focus} onClick={generate}>{t.genBtn}</button>
            </div>
            <div style={{fontSize:11,color:MUTED,textAlign:"center",marginTop:".5rem"}}>{t.genNote}</div>
          </div>
        )}

        {/* ── LOADING ── */}
        {phase==="generating" && (
          <div style={s.loadWrap}>
            <div style={s.spinner}/>
            <div style={s.loadBrand}>{project?.nombre}</div>
            <div style={s.loadTxt}>{msgs[loadMsg]}</div>
          </div>
        )}

        {/* ── RESULTS ── */}
        {phase==="results" && result && (
          <div className="fi">
            <div style={s.topLbl}>{t.resLbl}</div>
            <div style={s.pageTitle}>{t.resTitle}</div>

            <div style={s.chips}>
              {focusObj && <span style={s.chip("v")}>{t.focusChipLbl}: {focusObj.label}{tema&&` · ${tema}`}</span>}
              {mode==="product" && campObj && <span style={s.chip("p")}>{t.productBadge}: {campObj.label}</span>}
              {mode==="person" && <span style={s.chip("p")}>{t.personBadge}</span>}
              {mode==="space" && <span style={s.chip("p")}>{t.spaceBadge}</span>}
            </div>

            <div style={s.stratBox}>
              <div style={s.stratDot}/>
              <div style={s.stratTxt}>{result.resumen_semana}</div>
            </div>

            <div style={s.dayTabs}>
              {days.map((d,i)=>(
                <div key={i} style={s.dayTab(activeDay===i)} onClick={()=>setActiveDay(i)}>{d}</div>
              ))}
            </div>

            {currentDay && (
              <div style={s.dayCard} key={activeDay}>
                <div style={s.dayHeader}>
                  <div>
                    <div style={s.dayNum}>{days[activeDay]}</div>
                    <div style={s.dayTema}>{currentDay.tema_dia}</div>
                  </div>
                  <div style={s.dayRight}>
                    <div style={s.fmtBadge(currentDay.formato)}>{currentDay.formato}</div>
                    <div style={s.dimBadge}>{currentDay.dimensions?.size}</div>
                  </div>
                </div>

                {currentDay.por_que && (
                  <div style={{background:V_SOFT,border:`1px solid ${V_MID}`,borderRadius:12,padding:"12px 16px",margin:"0 0 4px",fontSize:16,color:TEXT,lineHeight:1.7,fontFamily:B}}>
                    <span style={{fontWeight:700,color:V}}>{es?"Por qué hoy: ":"Why today: "}</span>{currentDay.por_que}
                  </div>
                )}

                <div style={s.dayBody}>
                  <div>
                    {/* HOOK */}
                    <div style={s.fieldBlock}>
                      <div style={s.miniLbl}>{t.hook}</div>
                      <div style={{fontSize:14,fontWeight:500,color:MUTED,fontFamily:B,lineHeight:1.6,marginBottom:10,marginTop:3}}>{t.hookHelp}</div>
                      <div style={s.contentRow}>
                        <div style={s.contentTxt}>{currentDay.hook}</div>
                        <button style={s.copyBtn(copied===`h${activeDay}`)} onClick={()=>copy(currentDay.hook,`h${activeDay}`)}>{copied===`h${activeDay}`?t.copied:t.copy}</button>
                      </div>

                      {/* STORYTELLING for videos */}
                      {IS_VIDEO(currentDay.formato) && currentDay.storytelling && (
                        <div style={s.storyBox}>
                          <div style={s.storyTitle}>{t.story}</div>
                          {currentDay.storytelling.split("|").map((part,i)=>{
                            const labels = [t.storyIntro, t.storyDev, t.storyClose]
                            const cleaned = part.replace(/\[(INTRO 5s|DESARROLLO 20s|CIERRE|INTRO|DEVELOPMENT|CLOSING)[^\]]*\]:/i,"").trim()
                            return cleaned ? (
                              <div key={i} style={s.storyPart}>
                                <div style={s.storyLbl}>{labels[i] || ""}</div>
                                <div style={s.storyTxt}>{cleaned}</div>
                              </div>
                            ) : null
                          })}
                          <div style={{marginTop:6,display:"flex",justifyContent:"flex-end"}}>
                            <button style={s.copyBtn(copied===`st${activeDay}`)} onClick={()=>copy(currentDay.storytelling,`st${activeDay}`)}>{copied===`st${activeDay}`?t.copied:t.copy}</button>
                          </div>
                        </div>
                      )}

                      {IS_VIDEO(currentDay.formato) && <div style={s.videoNote}>{t.videoNote}</div>}
                    </div>

                    <div style={s.divLine}/>

                    {/* CTA */}
                    <div style={s.fieldBlock}>
                      <div style={s.miniLbl}>{t.cta}</div>
                      <div style={{fontSize:14,fontWeight:500,color:MUTED,fontFamily:B,lineHeight:1.6,marginBottom:10,marginTop:3}}>{t.ctaHelp}</div>
                      <div style={s.contentRow}>
                        <div style={s.ctaTxt}>{currentDay.cta}</div>
                        <button style={s.copyBtn(copied===`c${activeDay}`)} onClick={()=>copy(currentDay.cta,`c${activeDay}`)}>{copied===`c${activeDay}`?t.copied:t.copy}</button>
                      </div>
                    </div>

                    {currentDay.como_usar && (
                      <div style={{background:V_SOFT,border:`1px solid ${V_MID}`,borderRadius:12,padding:"12px 16px",marginTop:10,fontSize:16,color:TEXT,lineHeight:1.7,fontFamily:B}}>
                        <span style={{fontWeight:700,color:V}}>{es?"Cómo usarlo: ":"How to use it: "}</span>{currentDay.como_usar}
                      </div>
                    )}
                  </div>

                  {/* IMAGE COL */}
                  <div style={s.imgCol}>
                    <div style={s.imgColLbl}>{es?"Imagen":"Image"}</div>
                    {currentDay.img_urls && currentDay.img_urls.length > 0 ? (
                      <div style={{marginBottom:5}}>
                        {currentDay.img_urls.map((url, i) => (
                          <img key={i} src={url} alt={`carrusel ${i+1}`} style={{width:"100%",height:"auto",display:"block",borderRadius:8,marginBottom:4}}/>
                        ))}
                      </div>
                    ) : currentDay.img_url ? (
                      <img src={currentDay.img_url} alt={es?"Imagen generada":"Generated image"} style={{width:"100%",height:"auto",display:"block",borderRadius:8,marginBottom:5}}/>
                    ) : (
                      <ImageMockup estilo={result?.estilo_sugerido} nombre={project?.nombre.split(" ")[0]} concepto={currentDay.img_idea} formato={currentDay.formato} small hasProduct={mode==="product"} hasPerson={mode==="person"}/>
                    )}
                    {!currentDay.img_approved && (
                      <div style={{ fontSize:12, color:TEXT, fontFamily:B, lineHeight:1.55, marginBottom:8, padding:"8px 10px", background:V_SOFT, borderRadius:8, border:`1px solid ${V_MID}` }}>
                        {es ? "Tenemos un concepto visual pensado para esta pieza. Da click para verlo y crearlo juntos." : "We have a visual concept designed for this piece. Click to see it and create it together."}
                      </div>
                    )}
                    <button style={s.imgGenBtn(currentDay.img_approved)} onClick={()=>!currentDay.img_approved&&openImgFlow(activeDay)}>
                      {currentDay.img_approved ? t.imgApproved : t.genImg}
                    </button>
                    {currentDay.img_url && (
                      <button style={{...s.imgGenBtn(false), marginTop:5}} onClick={()=>descargarImagen(currentDay.img_url, "koriva-imagen.png")}>
                        {es?"Descargar imagen ↓":"Download image ↓"}
                      </button>
                    )}
                    {currentDay.img_urls && currentDay.img_urls.length > 0 && currentDay.img_urls.map((url, i) => (
                      <button key={i} style={{...s.imgGenBtn(false), marginTop:5}} onClick={()=>descargarImagen(url, "koriva-imagen-" + (i+1) + ".png")}>
                        {es?`Descargar imagen ${i+1} ↓`:`Download image ${i+1} ↓`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div style={s.divider}/>
            <div style={s.counterRow}>
              <span style={s.counterLbl}>{t.imgCounter}</span>
              <span style={s.counterNum}>{imgTotal-imgUsed}/{imgTotal}</span>
            </div>
            <button style={s.newWeekBtn} onClick={()=>{setPhase("mode");setResult(null);setFocus(null);setTema("");setMode(null);setProductPhoto(null);setProductAnalysis(null);setCampaignType(null);setPersonPhoto(null);setSpacePhoto(null);setSpaceType(null);setSpaceHighlight("");setSpaceAvoid("");setActiveDay(0);setShowTextImg(false)}}>
              {t.newWeek}
            </button>
          </div>
        )}

        {/* ── IMAGE FLOW ── */}
        {phase==="imgflow" && currentDay && (
          <div className="fi">
            <div style={s.topLbl}>{t.imgFlowLbl}</div>
            <div style={s.pageTitle}>{t.imgFlowTitle}</div>
            <p style={s.pageSub}>{t.imgFlowSub}</p>

            <div style={s.autoBox}>
              <div style={s.autoLbl}>{es?"Concepto generado automáticamente":"Automatically generated concept"}</div>
              <div style={s.autoTxt}>{editedPrompt}</div>
              <div style={s.dimInfo}>
                {currentDay.dimensions?.size} · {currentDay.dimensions?.label}
                &nbsp;·&nbsp; {result?.estilo_sugerido}
                {result?.estilo_sugerido==="Animado 3D" && (
                  <span style={{marginLeft:8,fontSize:10,fontWeight:700,color:V,background:V_SOFT,border:`1px solid ${V_MID}`,borderRadius:10,padding:"2px 8px",fontFamily:B}}>
                    2 {es?"créditos":"credits"}
                  </span>
                )}
              </div>
            </div>

            {/* Tip pedagógico: cómo agregar capas sin romper la coherencia */}
            <div style={{marginTop:12, padding:"14px 16px", background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:10}}>
              <div style={{fontSize:14, fontWeight:700, color:V, fontFamily:B, marginBottom:8, letterSpacing:".06em"}}>TIP</div>
              <div style={{fontSize:16, color:TEXT, fontFamily:B, lineHeight:1.6, marginBottom:10}}>
                {es
                  ? "El concepto que ves arriba ya está pensado para que tenga sentido con toda tu semana. Para hacerlo más tuyo, agrega capas con los controles de abajo:"
                  : "The concept above is designed to make sense with your entire week. To personalize it, add layers using the controls below:"}
              </div>
              <ul style={{margin:0, paddingLeft:18, fontSize:16, color:TEXT, fontFamily:B, lineHeight:1.7}}>
                <li style={{marginBottom:6}}>
                  {es ? <>Para que aparezca tu personaje: <strong>menciónalo en los ajustes, no cambies el concepto</strong> (ej. <em>"agrega a mi personaje en la escena"</em>) Y márcalo abajo en la sección de personajes.</> : <>For your character to appear: <strong>mention it in the adjustments, don't change the concept</strong> (e.g. <em>"add my character to the scene"</em>) AND check it below in the characters section.</>}
                </li>
                <li style={{marginBottom:6}}>
                  {es ? <>Para incluir tu logo: márcalo abajo en "Incluir logo en la imagen".</> : <>To include your logo: check it below in "Include logo in image".</>}
                </li>
                <li style={{marginBottom:6}}>
                  {es ? <>Para agregar texto: copia tu hook o CTA y pégalo en "Texto en la imagen". Elige una fuente y posición que combine con tu marca.</> : <>To add text: copy your hook or CTA and paste it in "Text in image". Choose a font and position that fits your brand.</>}
                </li>
                <li>
                  {es ? <>Para revelar que la imagen fue generada con inteligencia artificial: márcalo abajo. El Reglamento (UE) 2024/1689 se aplica según dónde se ve tu contenido, no dónde está tu negocio: si tu publicación llega a personas en la Unión Europea, aunque tu empresa esté en otro país, estás dentro de su alcance. En ese caso, si la imagen representa algo real que podría parecer auténtico, una persona, un producto, un lugar o un hecho, <strong>tienes que revelar de forma clara y visible</strong> que fue generada o manipulada con inteligencia artificial. Lo obligatorio es revelar; la forma la eliges tú: el icono oficial de la Comisión Europea, que KORIVA incorpora en la imagen, o un texto en el pie de tu publicación. Elige AI GENERATED si la imagen se creó desde cero, o AI MODIFIED si incluye una foto real tuya, de tu producto o de tu espacio.</> : <>To disclose that the image was generated with artificial intelligence: check it below. Regulation (EU) 2024/1689 applies based on where your content is seen, not where your business is: if your post reaches people in the European Union, even if your company is in another country, you are within its scope. In that case, if the image depicts something real that could appear authentic, a person, a product, a place or an event, <strong>you must clearly and visibly disclose</strong> that it was generated or manipulated with artificial intelligence. What is required is the disclosure; the form is your choice: the official European Commission label, which KORIVA embeds in the image, or a line in your post caption. Choose AI GENERATED if the image was created from scratch, or AI MODIFIED if it includes a real photo of you, your product or your space.</>}
                </li>
              </ul>
            </div>

            {/* Dropdown del estilo de imagen (sugerido por KORIVA, editable) */}
            {imgPhase==="prompt" && (
              <div style={{ marginTop:12, marginBottom:12, padding:"12px 14px", background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:10 }}>
                <div style={{ fontSize:15, fontWeight:700, color:TEXT, fontFamily:H, marginBottom:5 }}>
                  {es?"Estilo visual de esta pieza":"Visual style for this piece"}
                </div>
                <div style={{ fontSize:15, color:MUTED, fontFamily:B, marginBottom:10 }}>
                  {es?"Sugerido por KORIVA según tu marca y el enfoque de la semana. Puedes cambiarlo.":"Suggested by KORIVA based on your brand and weekly focus. You can change it."}
                </div>
                <select
                  value={result?.estilo_sugerido || "Editorial"}
                  onChange={async (e)=>{
                    const nuevo = e.target.value
                    setResult({...result, estilo_sugerido: nuevo})
                    if (result?.week_id) {
                      try { await supabase.from("weeks").update({ estilo_sugerido: nuevo }).eq("id", result.week_id) }
                      catch(err) { console.error("No se pudo guardar el estilo:", err) }
                    }
                  }}
                  style={{ width:"100%", padding:"12px 16px", borderRadius:8, border:`1px solid ${V_MID}`, fontFamily:B, fontSize:15, boxSizing:"border-box", background:"#fff", cursor:"pointer" }}>
                  <option value="Editorial">Editorial</option>
                  <option value="Lifestyle">Lifestyle</option>
                  <option value="Minimalista">Minimalista</option>
                  <option value="Ilustración">Ilustración</option>
                  <option value="Cinematic">Cinematic</option>
                  <option value="Bold">Bold</option>
                  <option value="Animado 3D">Animado 3D</option>
                </select>
              </div>
            )}

            {/* Selector de personajes con casillas (puedes marcar 1, 2 o 3) */}
            {personajes.length > 0 && imgPhase==="prompt" && (
              <div style={{ marginTop:12, marginBottom:12, padding:"12px 14px", background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:10 }}>
                <div style={{ fontSize:15, fontWeight:700, color:TEXT, fontFamily:H, marginBottom:5 }}>
                  {es?"¿Quieres usar tus personajes? (cuesta 2 créditos)":"Use your characters? (costs 2 credits)"}
                </div>
                <div style={{ fontSize:15, color:MUTED, fontFamily:B, marginBottom:10 }}>
                  {es?"Marca uno o varios. Si dejas todos vacíos, no usaremos personaje.":"Mark one or more. Leave all empty for no character."}
                  {personajesElegidos.length > 0 && (
                    <span style={{ color:V, fontWeight:700, marginLeft:6 }}>
                      ({personajesElegidos.length} {es?"seleccionado(s)":"selected"})
                    </span>
                  )}
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"center" }}>
                  {personajes.map(p => {
                    const marcado = personajesElegidos.includes(p.id)
                    return (
                      <div key={p.id} onClick={()=>toggleCharacter(p.id)}
                        style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 12px 5px 5px", borderRadius:8, cursor:"pointer",
                          border: marcado ? `2px solid ${V}` : `1px solid ${V_MID}`,
                          background: marcado ? "#fff" : "transparent",
                          position:"relative" }}>
                        <div style={{ width:22, height:22, borderRadius:5, border:`2px solid ${marcado ? V : V_MID}`, background: marcado ? V : "#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {marcado && <span style={{ color:"#fff", fontSize:14, fontWeight:900, lineHeight:1 }}>✓</span>}
                        </div>
                        <img src={p.imagen_url} alt={p.nombre} style={{ width:96, height:96, borderRadius:10, objectFit:"cover" }}/>
                        <span style={{ fontSize:14, fontFamily:B, fontWeight: marcado ? 700 : 500, color: marcado ? V : TEXT }}>{p.nombre}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Casilla: incluir el logo del proyecto (independiente de personajes) */}
            {logoProyecto && logoProyecto.listoParaUsar && imgPhase==="prompt" && (
              <div style={{ marginTop:12, marginBottom:12, padding:"12px 14px", background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:10 }}>
                <div onClick={()=>setIncluirLogo(v=>!v)}
                  style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                  <div style={{ width:22, height:22, borderRadius:5, border:`2px solid ${incluirLogo ? V : V_MID}`, background: incluirLogo ? V : "#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    {incluirLogo && <span style={{ color:"#fff", fontSize:14, fontWeight:900, lineHeight:1 }}>✓</span>}
                  </div>
                  <img src={logoProyecto.base64 || logoProyecto.url} alt="logo" style={{ width:96, height:96, borderRadius:10, objectFit:"contain", background:"#f5f5f5", padding:8 }}/>
                  <span style={{ fontSize:14, fontFamily:B, fontWeight: incluirLogo ? 700 : 500, color: incluirLogo ? V : TEXT }}>{es?"Incluir logo en la imagen":"Include logo in the image"}</span>
                </div>
              </div>
            )}

            {/* Casillas: icono oficial de transparencia IA (Reglamento UE 2024/1689) */}
            {imgPhase==="prompt" && (
              <div style={{ marginTop:12, marginBottom:12, padding:"12px 14px", background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:10 }}>
                <div style={{ fontSize:16, fontWeight:700, color:TEXT, fontFamily:H, marginBottom:4 }}>
                  {es?"Revelar que la imagen fue generada con IA":"Disclose that the image was AI-generated"}
                </div>
                <div style={{ fontSize:15, color:MUTED, fontFamily:B, marginBottom:12, lineHeight:1.6 }}>
                  {es?"Si tu contenido llega a personas en la Unión Europea y la imagen representa algo real, el Reglamento (UE) 2024/1689 te obliga a revelarlo de forma clara y visible. El icono oficial es una de las formas de hacerlo.":"If your content reaches people in the European Union and the image depicts something real, Regulation (EU) 2024/1689 requires you to disclose it clearly and visibly. The official label is one way to do it."}
                </div>

                <div onClick={()=>setIconoAI(v => v==="generated" ? null : "generated")}
                  style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", marginBottom:12 }}>
                  <div style={{ width:22, height:22, borderRadius:5, border:`2px solid ${iconoAI==="generated" ? V : V_MID}`, background: iconoAI==="generated" ? V : "#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:2 }}>
                    {iconoAI==="generated" && <span style={{ color:"#fff", fontSize:14, fontWeight:900, lineHeight:1 }}>✓</span>}
                  </div>
                  <div>
                    <div style={{ fontSize:16, fontFamily:H, fontWeight:700, color: iconoAI==="generated" ? V : TEXT, marginBottom:2 }}>AI GENERATED</div>
                    <div style={{ fontSize:15, color:MUTED, fontFamily:B, lineHeight:1.6 }}>
                      {es?"Úsalo si la imagen se creó desde cero, sin fotos tuyas.":"Use it if the image was created from scratch, without your photos."}
                    </div>
                  </div>
                </div>

                <div onClick={()=>setIconoAI(v => v==="modified" ? null : "modified")}
                  style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer" }}>
                  <div style={{ width:22, height:22, borderRadius:5, border:`2px solid ${iconoAI==="modified" ? V : V_MID}`, background: iconoAI==="modified" ? V : "#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:2 }}>
                    {iconoAI==="modified" && <span style={{ color:"#fff", fontSize:14, fontWeight:900, lineHeight:1 }}>✓</span>}
                  </div>
                  <div>
                    <div style={{ fontSize:16, fontFamily:H, fontWeight:700, color: iconoAI==="modified" ? V : TEXT, marginBottom:2 }}>AI MODIFIED</div>
                    <div style={{ fontSize:15, color:MUTED, fontFamily:B, lineHeight:1.6 }}>
                      {es?"Úsalo si la imagen incluye una foto real: tu producto, una persona o tu espacio.":"Use it if the image includes a real photo: your product, a person or your space."}
                    </div>
                  </div>
                </div>

                <div style={{ fontSize:15, color:MUTED, fontFamily:B, marginTop:12, paddingTop:12, borderTop:`1px solid ${V_MID}`, lineHeight:1.6 }}>
                  {es?"Las imágenes en estilo Animado 3D, o cualquier escena evidentemente imaginaria, no se consideran contenido que pueda confundirse con la realidad. Todas las imágenes de KORIVA llevan además una marca de procedencia dentro del archivo. Esa marca no sustituye la revelación visible, y puede perderse si editas la imagen en otra plataforma y la vuelves a guardar.":"Images in Animated 3D style, or any evidently imaginary scene, are not considered content that could be mistaken for reality. Every KORIVA image also carries a provenance mark inside the file. That mark does not replace the visible disclosure, and it can be lost if you edit the image on another platform and save it again."}
                </div>
              </div>
            )}

            {/* TEXTO EN LA IMAGEN (opcional) */}
            <div style={{ marginTop:12, marginBottom:12, padding:"12px 14px", background:"#FAFAFA", border:`1px solid ${BORDER}`, borderRadius:10 }}>
              <div style={{ fontSize:16, fontWeight:700, color:TEXT, fontFamily:H, marginBottom:4 }}>
                {es?"Texto en la imagen (opcional)":"Text on the image (optional)"}
              </div>
              <div style={{ fontSize:15, color:MUTED, fontFamily:B, marginBottom:8 }}>
                {es?"Si escribes algo, KORIVA lo incluirá en la imagen con la fuente y posición que elijas.":"If you write something, KORIVA will include it in the image with the font and position you choose."}
              </div>
              <input type="text" value={textoImagen}
                onChange={e=>setTextoImagen(e.target.value)}
                placeholder={es?"Ej: Ven a degustar nuestro nuevo té":"E.g. Come and taste our new tea"}
                style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:`1px solid ${V_MID}`, fontFamily:B, fontSize:13, boxSizing:"border-box", marginBottom: textoImagen ? 12 : 0 }}/>

              {/* Solo mostrar fuente y posicion si el usuario escribio algo */}
              {textoImagen && textoImagen.trim() && (
                <>
                  <div style={{ fontSize:12, fontWeight:700, color:TEXT, fontFamily:H, marginBottom:6, marginTop:4 }}>
                    {es?"Elige la fuente":"Choose the font"}
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 }}>
                    {FUENTES_DISPONIBLES.map(f => {
                      const marcada = fuenteImagen === f.id
                      return (
                        <div key={f.id} onClick={()=>setFuenteImagen(f.id)}
                          style={{ padding:"8px 12px", borderRadius:8, cursor:"pointer",
                            border: marcada ? `2px solid ${V}` : `1px solid ${V_MID}`,
                            background: marcada ? "#fff" : "transparent",
                            lineHeight:1.2, textAlign:"center", minWidth:80 }}>
                          <div style={{ fontFamily: `'${f.id}', sans-serif`, fontSize:16, fontWeight:700, color: marcada ? V : TEXT }}>{f.id}</div>
                          <div style={{ fontSize:9, color: marcada ? V : MUTED, marginTop:3, fontFamily:B }}>{f.label}</div>
                        </div>
                      )
                    })}
                  </div>

                  <div style={{ fontSize:12, fontWeight:700, color:TEXT, fontFamily:H, marginBottom:6 }}>
                    {es?"Posición del texto":"Text position"}
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    {[
                      { id:"arriba", label: es?"Arriba":"Top" },
                      { id:"abajo",  label: es?"Abajo":"Bottom" },
                      { id:"lado",   label: es?"A un lado":"Side" }
                    ].map(p => {
                      const marcada = posicionTexto === p.id
                      return (
                        <div key={p.id} onClick={()=>setPosicionTexto(p.id)}
                          style={{ flex:1, padding:"7px 10px", borderRadius:8, cursor:"pointer", textAlign:"center",
                            border: marcada ? `2px solid ${V}` : `1px solid ${V_MID}`,
                            background: marcada ? "#fff" : "transparent",
                            fontSize:12, fontFamily:B, fontWeight: marcada ? 700 : 500,
                            color: marcada ? V : TEXT }}>
                          {p.label}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>

            {imgError && (
              <div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:9,padding:"10px 13px",fontSize:12,color:"#DC2626",lineHeight:1.5,marginBottom:".6rem",fontFamily:B}}>{imgError}</div>
            )}

            {imgPhase==="generating" && (
              <>
                <div style={{display:"flex",alignItems:"center",gap:10,background:V_SOFT,border:`1px solid ${V_MID}`,borderRadius:10,padding:"14px 16px",marginBottom:".6rem"}}>
                  <div style={{width:16,height:16,border:`2px solid ${V_MID}`,borderTop:`2px solid ${V}`,borderRadius:"50%",animation:"spin .8s linear infinite",flexShrink:0}}/>
                  <span style={{fontSize:13,color:V,fontFamily:B,fontWeight:600}}>{es?"Generando tu imagen — esto puede tomar un momento":"Generating your image — this may take a moment"}</span>
                </div>
                <div style={{padding:"12px 16px",background:V_SOFT,border:`1px solid ${V_MID}`,borderRadius:8,fontSize:13,color:TEXT,fontFamily:B,fontStyle:"italic",textAlign:"center",lineHeight:1.5,marginBottom:".75rem",transition:"opacity .3s"}}>
                  {wellnessMsgs[es?"es":"en"][wellnessIdx]}
                </div>
              </>
            )}

            {imgPhase==="prompt" && imgUsed>=imgTotal && (
              <div style={{background:V_SOFT,border:`1px solid ${V_MID}`,borderRadius:10,padding:"1rem 1.1rem",marginBottom:".75rem"}}>
                <div style={{fontFamily:H,fontSize:15,fontWeight:800,color:TEXT,marginBottom:".25rem",letterSpacing:"-.01em"}}>{t.noCreditsTitle}</div>
                <div style={{fontSize:13,color:MUTED,marginBottom:".75rem",fontFamily:B,lineHeight:1.5}}>{t.noCreditsSub}</div>
                <button style={s.approveBtn} onClick={()=>navigate("/dashboard")}>{t.noCreditsBtn}</button>
              </div>
            )}

            {imgPhase==="prompt" && imgUsed<imgTotal && (
              <>
                <div style={{background:V_SOFT,border:`1px solid ${V_MID}`,borderRadius:10,padding:"14px 18px",fontSize:14,color:TEXT,lineHeight:1.6,marginBottom:".75rem",fontFamily:B}}>{currentDay?.formato==="Carrusel" ? t.carouselNote : t.approveNote}</div>
                {currentDay?.formato==="Carrusel"
                  ? <button style={s.approveBtn} onClick={approveCarousel}>{t.carouselBtn}</button>
                  : <button style={s.approveBtn} onClick={approveImage}>{t.approveBtn}</button>}

                {/* TEXT IMAGE OPTION */}
                <div style={{background:OFF,border:`1px solid ${BORDER}`,borderRadius:10,padding:"1rem 1.1rem",marginBottom:".75rem"}}>
                  <div style={{fontFamily:H,fontSize:16,fontWeight:700,color:TEXT,marginBottom:".25rem",letterSpacing:"-.01em"}}>{t.textImgTitle}</div>
                  <div style={{fontSize:15,color:MUTED,marginBottom:".75rem",fontFamily:B}}>{t.textImgSub}</div>
                  {!showTextImg
                    ? <button style={{padding:"8px 16px",background:WHITE,border:`1.5px solid ${BORDER}`,borderRadius:8,fontFamily:H,fontSize:15,fontWeight:700,color:TEXT,cursor:"pointer"}} onClick={()=>setShowTextImg(true)}>
                        {t.textImgBtn}
                      </button>
                    : <div style={{background:WHITE,borderRadius:8,border:`1px solid ${BORDER}`,overflow:"hidden"}}>
                        <canvas id="text-img-canvas" width="600" height="600" style={{width:"100%",height:"auto",display:"block"}}/>
                        <div style={{padding:".65rem",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                          <div style={{fontSize:11,color:MUTED,fontFamily:B}}>{t.textImgNote}</div>
                          <button style={{padding:"7px 14px",background:V,color:WHITE,border:"none",borderRadius:7,fontFamily:H,fontSize:12,fontWeight:700,cursor:"pointer"}}
                            onClick={()=>{const a=document.createElement('a');a.download='imagen-texto.png';a.href=document.getElementById('text-img-canvas').toDataURL('image/png');a.click()}}>
                            {t.textImgDownload}
                          </button>
                        </div>
                      </div>
                  }
                </div>

                <button style={s.backBtn} onClick={()=>setPhase("results")}>{t.backContent}</button>
              </>
            )}

            {imgPhase==="final" && (
              <>
                <div style={{fontFamily:H,fontSize:20,fontWeight:800,color:TEXT,marginBottom:".4rem",letterSpacing:"-.02em"}}>{t.finalTitle}</div>
                <p style={{fontSize:13,color:MUTED,marginBottom:"1rem"}}>{t.finalSub}</p>

                {currentDay?.formato==="Carrusel" && currentDay?.img_urls && currentDay.img_urls.length > 0 ? (
                  <div style={{marginBottom:"1rem"}}>
                    <div style={{fontSize:11,fontWeight:700,color:TEXT,marginBottom:8,fontFamily:H}}>{es?"Tu carrusel":"Your carousel"}</div>
                    {currentDay.img_urls.map((url, i) => (
                      <div key={i} style={{marginBottom:".9rem"}}>
                        <div style={{fontSize:10,color:MUTED,fontWeight:700,marginBottom:4,fontFamily:B,textTransform:"uppercase",letterSpacing:".06em"}}>{es?"Imagen":"Image"} {i+1}</div>
                        <img src={url} alt={`carrusel ${i+1}`} style={{width:"100%",height:"auto",display:"block",borderRadius:8,marginBottom:6}}/>
                        <button style={{...s.imgGenBtn(false)}} onClick={()=>descargarImagen(url, "koriva-imagen-" + (i+1) + ".png")}>
                          {es?"Descargar imagen":"Download image"} {i+1} ↓
                        </button>
                      </div>
                    ))}
                    <button style={{...s.saveBtn, marginTop:".5rem"}} onClick={()=>setPhase("results")}>{t.saveBtn}</button>
                  </div>
                ) : (
                <>
                {/* BEFORE & AFTER */}
                <div style={{fontSize:11,fontWeight:700,color:TEXT,marginBottom:8,fontFamily:H}}>{t.beforeAfterLbl}</div>
                <div style={s.baGrid}>
                  <div style={s.baCard}>
                    <div style={s.baLbl}>{t.beforeLbl}</div>
                    {(mode==="product"&&productPhoto)||(mode==="person"&&personPhoto)
                      ? <img src={mode==="product"?productPhoto:personPhoto} alt="before" style={s.baImg}/>
                      : <div style={{height:140,background:OFF,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:MUTED,fontStyle:"italic",padding:"1rem",textAlign:"center"}}>{t.noPhotoYet}</div>
                    }
                  </div>
                  <div style={s.baCard}>
                    <div style={s.baLbl}>{t.afterLbl}</div>
                    {generatedImageUrl
                      ? <img src={generatedImageUrl} alt="generada" style={{width:"100%",height:"auto",display:"block",borderRadius:8}}/>
                      : <ImageMockup estilo={result?.estilo_sugerido} nombre={project?.nombre.split(" ")[0]} concepto={editedPrompt} formato={currentDay.formato} hasProduct={mode==="product"} hasPerson={mode==="person"}/>
                    }
                  </div>
                </div>

                <div style={s.regenRow}>
                  <button style={s.regenBtn(false)} onClick={approveImage}>
                    ↺ {t.regenBtn}
                  </button>
                </div>
                <div style={{fontSize:12,color:MUTED,marginBottom:".75rem",fontFamily:B,lineHeight:1.5}}>{t.regenNote}</div>
                <button style={s.saveBtn} onClick={()=>setPhase("results")}>{t.saveBtn}</button>
                </>
                )}
              </>
            )}
          </div>
        )}

      </div>
    </div>
  )
}
